import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckCircle, ChevronLeft, ChevronRight, Download, Heart, Loader2, Mic, RefreshCw, Send, Share2, Smile, Star, ThumbsUp, WifiOff, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import { Question, SurveyLanguage, SurveyPage } from './QuestionBuilder'
import {
  evaluateDisplayConditions,
  getFilteredQuestionOptions,
  getNextSurveyLocation,
  getPreviousVisibleQuestionIndex,
  getResolvedQuestionContent,
  getUnansweredRequiredQuestions,
  getVisibleMatrixColumns,
  getVisibleMatrixRows,
  getVisibleTextBoxes,
  isAnswerFilled,
  isQuestionAnswerComplete,
  resolveDynamicText,
} from '../lib/surveyRuntime'
import {
  addOfflineSubmission,
  base64ToFile,
  blobToBase64,
  downloadAudioDirectly,
  getOfflineQueueCount,
  isNetworkError,
  OfflineSubmission,
  saveAudioToIndexedDB,
  syncOfflineSubmissions,
} from '../lib/offlineSurveyQueue'
import { convertRecordedAudioToMp3 } from '../lib/audioMp3'
import {
  SURVEY_LANGUAGE_LABELS,
  autoTranslateHtml,
  autoTranslateText,
  ensureHtmlString,
  getImageDisplayHint,
  getImageDisplayWidthClass,
  getLogoDisplayImageClass,
  getLogoDisplayWidthClass,
  getLocalizedQuestionContent,
  localizeQuestionOptions,
  normalizeDisplayText,
  stripHtml,
} from '../lib/surveyPresentation'

export interface ResearcherSurvey {
  _id: string
  title: string
  description?: string
  logoUrl?: string
  logo_url?: string
  headerImageUrl?: string
  logo?: {
    url?: string
    fileUrl?: string
  } | string
  logoDisplaySize?: 'small' | 'medium' | 'large' | 'original'
  showSurveyTitle?: boolean
  showPageTitles?: boolean
  showLogoOnEveryPage?: boolean
  audioRecordingEnabled?: boolean
  questions?: Question[]
  pages?: SurveyPage[]
  languages?: SurveyLanguage[]
}

interface Props {
  survey: ResearcherSurvey
  researcherId?: string
  researcherName?: string
  testMode?: boolean
  isPublicLink?: boolean
  onClose?: () => void
  onCancelled?: (savedProgress: boolean) => void
  onSubmitted: () => void
}

type Answers = Record<string, any>

type RichTextAlign = 'left' | 'center' | 'right' | 'justify'

interface SurveyDraft {
  answers: Answers
  curIdx: number
  terminalPageId?: string | null
  introCompleted?: boolean
  visitedQuestionIds?: string[]
  audioDecisionMade: boolean
  audioConsent: boolean
  audioConsentAt: string | null
  language?: SurveyLanguage
}

interface SubmittedAudio {
  responseId: string
  audioUrl: string
  remoteAudioUrl: string
  audioName: string
  durationSec: number
  submittedAt: string
  blob: Blob
  file: File
}

interface SubmittedResult {
  responseId?: string
  audioSaved: boolean
  audio?: SubmittedAudio | null
}

const buildOrdered = (survey: ResearcherSurvey): Question[] => {
  const pages = survey.pages || []
  if (pages.length === 0) return survey.questions || []

  const ids = pages.flatMap((page) => page.questionIds || [])
  const questionMap = Object.fromEntries((survey.questions || []).map((question) => [question.id, question]))
  const orderedQuestions = ids.map((id) => questionMap[id]).filter(Boolean) as Question[]
  const addedQuestionIds = new Set(orderedQuestions.map((question) => question.id))
  const orphanQuestions = (survey.questions || []).filter((question) => !addedQuestionIds.has(question.id))

  return [...orderedQuestions, ...orphanQuestions]
}

const buildIndexMap = (questions: Question[]) => {
  const indexMap = new Map<string, number>()
  questions.forEach((question, index) => indexMap.set(question.id, index))
  return indexMap
}

const getSupportedMime = (): string => {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || ''
}

const getAudioExtension = (mimeType: string) => {
  if (mimeType.includes('ogg')) return 'ogg'
  if (mimeType.includes('mp4')) return 'mp4'
  if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return 'mp3'
  return 'webm'
}

const buildAudioFileName = (surveyId: string, responseId: string, mimeType: string) =>
  `${surveyId}_${responseId}.${getAudioExtension(mimeType)}`

const resolveSurveyLogoUrl = (survey: ResearcherSurvey) => {
  const candidates = [
    survey.logoUrl,
    survey.logo_url,
    survey.headerImageUrl,
    typeof survey.logo === 'string' ? survey.logo : undefined,
    typeof survey.logo === 'object' ? survey.logo?.fileUrl : undefined,
    typeof survey.logo === 'object' ? survey.logo?.url : undefined,
  ]

  return candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0)?.trim() || ''
}

const formatDuration = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const getDraftKey = (surveyId: string, researcherId: string) =>
  `researcher_survey_draft:${researcherId}:${surveyId}`

const getPendingKey = (surveyId: string, researcherId: string) =>
  `dna_pending_submission:${surveyId}:${researcherId}`


// ─────────────────────────────────────────────────────────────────────────────
const OTHER_OPTION_ID = '__other__'

const getOtherLabel = (question: Question, language: SurveyLanguage) =>
  autoTranslateText(question.otherLabel?.trim() || 'Outro', language)
const getOtherPlaceholder = (question: Question, language: SurveyLanguage) =>
  autoTranslateText(question.otherPlaceholder?.trim() || 'Digite sua resposta', language)

const getUiText = (language: SurveyLanguage, pt: string, en: string, es: string) => {
  if (language === 'en') return en
  if (language === 'es') return es
  return pt
}

const getRichTextAlignment = (html: string, fallback: RichTextAlign = 'left'): RichTextAlign => {
  const explicitAlignment = html.match(/ql-align-(left|center|right|justify)/)
  if (explicitAlignment?.[1]) {
    return explicitAlignment[1] as RichTextAlign
  }

  const inlineAlignment = html.match(/text-align\s*:\s*(left|center|right|justify)/i)
  if (inlineAlignment?.[1]) {
    return inlineAlignment[1].toLowerCase() as RichTextAlign
  }

  return fallback
}

const applyRichTextAlignment = (html: string, fallback: RichTextAlign = 'left') => {
  if (!html.trim()) return html

  return html.replace(/<(p|div|h[1-6]|li|blockquote)([^>]*)>/gi, (match, tagName: string, rawAttributes: string) => {
    const attributes = rawAttributes || ''
    const classMatch = attributes.match(/class=(["'])(.*?)\1/i)
    const className = classMatch?.[2] || ''
    const alignMatch = className.match(/\bql-align-(left|center|right|justify)\b/i)
    const inlineAlignMatch = attributes.match(/text-align\s*:\s*(left|center|right|justify)/i)
    const resolvedAlign = (inlineAlignMatch?.[1] || alignMatch?.[1] || fallback).toLowerCase() as RichTextAlign

    if (attributes.match(/\bstyle=(["'])(.*?)\1/i)) {
      const nextAttributes = attributes.replace(/\bstyle=(["'])(.*?)\1/i, (_styleMatch, quote: string, styleValue: string) => {
        const withoutTextAlign = String(styleValue)
          .replace(/(?:^|;)\s*text-align\s*:\s*(?:left|center|right|justify)\s*;?/gi, ';')
          .replace(/^;\s*/, '')
          .trim()
        const separator = withoutTextAlign && !withoutTextAlign.endsWith(';') ? '; ' : ''
        return `style=${quote}${withoutTextAlign}${separator}text-align: ${resolvedAlign};${quote}`
      })
      return `<${tagName}${nextAttributes}>`
    }

    return `<${tagName}${attributes} style="text-align: ${resolvedAlign};">`
  })
}

const normalizeRichTextLayoutSpaces = (html: string) =>
  html
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/\u00A0/g, ' ')

const RichTextBlock: React.FC<{
  html: string
  className?: string
  fallbackAlign?: RichTextAlign
  as?: 'div' | 'h2' | 'h3' | 'span'
}> = ({ html, className = '', fallbackAlign = 'left', as: Component = 'div' }) => {
  const layoutHtml = normalizeRichTextLayoutSpaces(html)

  return (
    <Component
      className={`survey-rich-text ql-editor min-w-0 max-w-full ${className}`}
      style={{ textAlign: getRichTextAlignment(layoutHtml, fallbackAlign) }}
      dangerouslySetInnerHTML={{ __html: applyRichTextAlignment(layoutHtml, fallbackAlign) }}
    />
  )
}

const translationMemory = new Map<string, string>()

const machineTranslatePlainText = async (value: string, language: SurveyLanguage) => {
  if (language === 'pt' || !value.trim()) return value

  const cacheKey = `${language}:${value}`
  const cachedValue = translationMemory.get(cacheKey)
  if (cachedValue !== undefined) return cachedValue

  try {
    const response = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=pt&tl=${language}&dt=t&q=${encodeURIComponent(value)}`
    )

    if (!response.ok) {
      throw new Error(`Falha HTTP ${response.status}`)
    }

    const payload = await response.json()
    const translated = Array.isArray(payload?.[0])
      ? payload[0].map((item: unknown[]) => item?.[0] || '').join('')
      : ''

    const normalizedTranslation = translated || autoTranslateText(value, language)
    translationMemory.set(cacheKey, normalizedTranslation)
    return normalizedTranslation
  } catch (error) {
    console.warn('Falha na traducao automatica. Usando fallback local:', error)
    const fallback = autoTranslateText(value, language)
    translationMemory.set(cacheKey, fallback)
    return fallback
  }
}

const machineTranslateHtml = async (value: unknown, language: SurveyLanguage) => {
  const normalizedValue = ensureHtmlString(value)
  if (language === 'pt' || !normalizedValue.trim()) return normalizedValue

  const parts = normalizedValue.split(/(<[^>]+>)/g)
  const translatedParts = await Promise.all(parts.map((part) => (
    part.startsWith('<') && part.endsWith('>')
      ? part
      : machineTranslatePlainText(part, language)
  )))

  return translatedParts.join('')
}

const buildTranslationItems = (survey: ResearcherSurvey, questions: Question[], pages: SurveyPage[]) => {
  const items: Array<{ key: string; value: unknown }> = [
    { key: 'survey:title', value: survey.title },
    { key: 'survey:description', value: survey.description || '' },
  ]

  pages.forEach((page) => {
    items.push({ key: `page:${page.id}:title`, value: page.title || '' })
    items.push({ key: `page:${page.id}:description`, value: page.description || '' })
  })

  questions.forEach((question) => {
    items.push({ key: `question:${question.id}:title`, value: question.title || '' })
    items.push({ key: `question:${question.id}:description`, value: question.description || '' })
    items.push({ key: `question:${question.id}:otherLabel`, value: question.otherLabel || 'Outro' })
    items.push({ key: `question:${question.id}:otherPlaceholder`, value: question.otherPlaceholder || 'Digite sua resposta' })

    ;(question.options || []).forEach((option) => {
      items.push({ key: `question:${question.id}:option:${option.id}`, value: option.text || '' })
    })

    ;(question.textBoxes || []).forEach((box) => {
      items.push({ key: `question:${question.id}:textBox:${box.id}:label`, value: box.label || '' })
      items.push({ key: `question:${question.id}:textBox:${box.id}:placeholder`, value: box.placeholder || '' })
    })

    ;(question.matrixRows || []).forEach((row) => {
      items.push({ key: `question:${question.id}:matrixRow:${row.id}`, value: row.label || '' })
    })

    ;(question.matrixColumns || []).forEach((column) => {
      items.push({ key: `question:${question.id}:matrixColumn:${column.id}`, value: column.label || '' })
    })
  })

  const deduped = new Map<string, unknown>()
  items.forEach((item) => {
    if (ensureHtmlString(item.value).trim()) {
      deduped.set(item.key, item.value)
    }
  })

  return Array.from(deduped, ([key, value]) => ({ key, value }))
}

const getAnswerValues = (answer: any): string[] => {
  if (Array.isArray(answer)) return answer.map((item) => String(item))
  if (answer && typeof answer === 'object' && Array.isArray(answer.values)) {
    return answer.values.map((item: any) => String(item))
  }
  if (answer && typeof answer === 'object' && answer.value !== undefined) {
    return [String(answer.value)]
  }
  if (answer === undefined || answer === null || answer === '') return []
  return [String(answer)]
}

const getSingleAnswerValue = (answer: any) => getAnswerValues(answer)[0] || ''
const getOtherText = (answer: any) => (
  answer && typeof answer === 'object' && typeof answer.otherText === 'string'
    ? answer.otherText
    : ''
)

const buildSingleAnswer = (value: string, otherText = '') => (
  value === OTHER_OPTION_ID ? { value, otherText } : value
)

const buildMultipleAnswer = (values: string[], otherText = '') => (
  values.includes(OTHER_OPTION_ID) ? { values, otherText } : values
)

const ScaleLabel: React.FC<{
  label?: string
  fallback: string | number
  className?: string
}> = ({ label, fallback, className = '' }) => {
  const html = ensureHtmlString(label)
  const cleaned = normalizeDisplayText(stripHtml(html)).trim()

  if (!cleaned) {
    return <span className={className}>{String(fallback ?? '')}</span>
  }

  return (
    <span
      className={`survey-rich-text min-w-0 max-w-full ${className}`}
      dangerouslySetInnerHTML={{ __html: normalizeRichTextLayoutSpaces(html) }}
    />
  )
}

const shouldFillRatingIcon = (icon: Question['ratingIcon'] | undefined, active: boolean) => {
  // Mantem o desenho preenchido em todos os icones ativos (inclui smile/thumb) para nao "apagar" o rosto
  return active
}

const SURVEY_APPLICATION_LANGUAGES: SurveyLanguage[] = ['pt', 'en', 'es']

const shuffleOptions = (options: Question['options'] = []) => {
  const shuffled = [...options]
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1))
    const current = shuffled[index]
    shuffled[index] = shuffled[swapIndex]
    shuffled[swapIndex] = current
  }
  return shuffled
}

const buildRandomizedOptions = (question: Question, options: Question['options'] = []) => {
  if (!question.randomizeOptions || options.length <= 1) return options

  const fixedLastCount = Math.min(
    options.length,
    Math.max(0, Math.floor(Number(question.randomizeFixedLastCount || 0)))
  )
  const randomizableCount = options.length - fixedLastCount

  if (randomizableCount <= 1) return options

  const randomizableOptions = options.slice(0, randomizableCount)
  const fixedTailOptions = fixedLastCount > 0 ? options.slice(randomizableCount) : []

  return [...shuffleOptions(randomizableOptions), ...fixedTailOptions]
}

const buildRandomizedList = <T,>(question: Question, items: T[] = []): T[] => {
  if (!question.randomizeOptions || items.length <= 1) return items

  const fixedLastCount = Math.min(
    items.length,
    Math.max(0, Math.floor(Number(question.randomizeFixedLastCount || 0)))
  )
  const randomizableCount = items.length - fixedLastCount
  if (randomizableCount <= 1) return items

  const head = items.slice(0, randomizableCount)
  const tail = fixedLastCount > 0 ? items.slice(randomizableCount) : []
  const shuffledHead = [...head]
  for (let index = shuffledHead.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1))
    const current = shuffledHead[index]
    shuffledHead[index] = shuffledHead[swapIndex]
    shuffledHead[swapIndex] = current
  }
  return [...shuffledHead, ...tail]
}

interface AnswerInputProps {
  question: Question
  questions: Question[]
  answers: Answers
  answer: any
  language: SurveyLanguage
  translatedTexts: Record<string, string>
  onChange: (value: any) => void
}

const AnswerInput: React.FC<AnswerInputProps> = ({
  question,
  questions,
  answers,
  answer,
  language,
  translatedTexts,
  onChange,
}) => {
  const randomizedOptionCacheRef = useRef<Record<string, Question['options']>>({})
  const randomizedRowCacheRef = useRef<Record<string, any[]>>({})
  const randomizedColumnCacheRef = useRef<Record<string, any[]>>({})
  const uiText = (pt: string, en: string, es: string) => normalizeDisplayText(getUiText(language, pt, en, es))
  const filteredOptions = getFilteredQuestionOptions(question, questions, answers)
  const filteredOptionKey = filteredOptions.map((option) => String(option.id)).join('|')
  const randomizationKey = [
    question.id,
    question.randomizeOptions ? 'random' : 'ordered',
    question.randomizeFixedLastCount || 0,
    filteredOptionKey,
  ].join(':')
  const options = useMemo(() => {
    if (!question.randomizeOptions) {
      delete randomizedOptionCacheRef.current[randomizationKey]
      return localizeQuestionOptions(filteredOptions, language).map((option) => ({
        ...option,
        text: translatedTexts[`question:${question.id}:option:${option.id}`] || option.text,
      }))
    }

    const cachedOptions = randomizedOptionCacheRef.current[randomizationKey]
    if (!cachedOptions) {
      randomizedOptionCacheRef.current[randomizationKey] = buildRandomizedOptions(question, filteredOptions)
    }

    return localizeQuestionOptions(randomizedOptionCacheRef.current[randomizationKey], language).map((option) => ({
      ...option,
      text: translatedTexts[`question:${question.id}:option:${option.id}`] || option.text,
    }))
  }, [filteredOptions, language, question, randomizationKey, translatedTexts])
  const otherLabel = translatedTexts[`question:${question.id}:otherLabel`] || getOtherLabel(question, language)
  const otherPlaceholder = translatedTexts[`question:${question.id}:otherPlaceholder`] || getOtherPlaceholder(question, language)

  const getRuntimeMatrixRows = () => {
    const visibleRows = getVisibleMatrixRows(question, questions, answers)
    if (!question.randomizeOptions) {
      delete randomizedRowCacheRef.current[question.id]
      return visibleRows
    }
    const key = `${question.id}|rows|${question.randomizeFixedLastCount || 0}|${visibleRows.map((row) => row.id).join(',')}`
    if (!randomizedRowCacheRef.current[key]) {
      randomizedRowCacheRef.current[key] = buildRandomizedList(question, visibleRows)
    }
    return randomizedRowCacheRef.current[key]
  }

  const getRuntimeMatrixColumns = () => {
    // Colunas sempre fixas: randomizeOptions afeta apenas as linhas (rows).
    // Independente de question.randomizeOptions, colunas nao sao embaralhadas.
    delete randomizedColumnCacheRef.current[question.id]
    return getVisibleMatrixColumns(question, questions, answers)
  }

  if (question.type === 'text' || question.type === 'opentext') {
    return (
      <textarea
        value={answer ?? ''}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={uiText('Digite sua resposta...', 'Type your answer...', 'Escriba su respuesta...')}
      />
    )
  }

  if (question.type === 'number' || question.type === 'email') {
    return (
      <input
        type={question.type === 'number' ? 'number' : 'email'}
        value={answer ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={uiText('Digite sua resposta...', 'Type your answer...', 'Escriba su respuesta...')}
      />
    )
  }

  if (question.type === 'single') {
    const selectedValue = getSingleAnswerValue(answer)
    const otherSelected = selectedValue === OTHER_OPTION_ID

    return (
      <div className="space-y-2">
        {options.map((option) => (
          <label key={option.id} className="flex cursor-pointer items-start gap-3 rounded-lg p-2 hover:bg-gray-50">
            <input
              type="radio"
              name={`q_${question.id}`}
              value={option.id}
              checked={selectedValue === option.id}
              onChange={() => onChange(option.id)}
              className="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
            />
            <span className="survey-rich-text min-w-0 flex-1 break-normal text-sm text-gray-700 [overflow-wrap:normal] [&_*]:max-w-full" dangerouslySetInnerHTML={{ __html: option.text }} />
          </label>
        ))}
        {question.allowOther && (
          <div className="rounded-lg border border-gray-200 p-2">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="radio"
                name={`q_${question.id}`}
                value={OTHER_OPTION_ID}
                checked={otherSelected}
                onChange={() => onChange(buildSingleAnswer(OTHER_OPTION_ID, getOtherText(answer)))}
                className="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
              />
              <span className="min-w-0 flex-1 break-normal text-sm text-gray-700 [overflow-wrap:normal]">{stripHtml(otherLabel)}</span>
            </label>
            {otherSelected && (
              <input
                type="text"
                value={getOtherText(answer)}
                onChange={(event) => onChange(buildSingleAnswer(OTHER_OPTION_ID, event.target.value))}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={stripHtml(otherPlaceholder)}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  if (question.type === 'multiple' || question.type === 'checkbox') {
    const selected = getAnswerValues(answer)
    const otherSelected = selected.includes(OTHER_OPTION_ID)
    const minSelections = question.validation?.minSelections
    const maxSelections = question.validation?.maxSelections
    const selectionHint = [
      typeof minSelections === 'number' ? uiText(`mínimo ${minSelections}`, `minimum ${minSelections}`, `mínimo ${minSelections}`) : null,
      typeof maxSelections === 'number' ? uiText(`máximo ${maxSelections}`, `maximum ${maxSelections}`, `máximo ${maxSelections}`) : null,
    ].filter(Boolean).join(', ')

    return (
      <div className="space-y-2">
        {selectionHint && (
          <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800">
            {uiText(
              `Selecione ${selectionHint} opção(oes).`,
              `Select ${selectionHint} option(s).`,
              `Seleccione ${selectionHint} opcion(es).`
            )}
          </p>
        )}
        {options.map((option) => (
          <label key={option.id} className="flex cursor-pointer items-start gap-3 rounded-lg p-2 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={(event) => {
                if (event.target.checked) {
                  if (option.isExclusive) {
                    onChange([option.id])
                    return
                  }

                  const withoutExclusive = selected.filter((selectedId) => {
                    const selectedOption = options.find((item) => item.id === selectedId)
                    return !selectedOption?.isExclusive
                  })

                  if (
                    typeof maxSelections === 'number' &&
                    withoutExclusive.length >= maxSelections &&
                    !selected.includes(option.id)
                  ) {
                    toast.error(uiText(
                      `Selecione no máximo ${maxSelections} opção(oes).`,
                      `Select at most ${maxSelections} option(s).`,
                      `Seleccione como máximo ${maxSelections} opcion(es).`
                    ))
                    return
                  }

                  onChange(buildMultipleAnswer([...withoutExclusive, option.id], getOtherText(answer)))
                  return
                }

                onChange(buildMultipleAnswer(selected.filter((value) => value !== option.id), getOtherText(answer)))
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded text-blue-600"
            />
            {question.trackSelectionOrder && selected.includes(option.id) && (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                {selected.indexOf(option.id) + 1}
              </span>
            )}
            <span className="survey-rich-text min-w-0 flex-1 break-normal text-sm text-gray-700 [overflow-wrap:normal] [&_*]:max-w-full" dangerouslySetInnerHTML={{ __html: option.text }} />
          </label>
        ))}
        {question.allowOther && (
          <div className="rounded-lg border border-gray-200 p-2">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={otherSelected}
                onChange={(event) => {
                  if (event.target.checked) {
                    const withoutExclusive = selected.filter((selectedId) => {
                      const selectedOption = options.find((item) => item.id === selectedId)
                      return !selectedOption?.isExclusive
                    })

                    if (
                      typeof maxSelections === 'number' &&
                      withoutExclusive.length >= maxSelections &&
                      !withoutExclusive.includes(OTHER_OPTION_ID)
                    ) {
                      toast.error(uiText(
                        `Selecione no máximo ${maxSelections} opção(oes).`,
                        `Select at most ${maxSelections} option(s).`,
                        `Seleccione como máximo ${maxSelections} opcion(es).`
                      ))
                      return
                    }

                    onChange(buildMultipleAnswer([...withoutExclusive, OTHER_OPTION_ID], getOtherText(answer)))
                    return
                  }

                  onChange(buildMultipleAnswer(selected.filter((value) => value !== OTHER_OPTION_ID), getOtherText(answer)))
                }}
                className="mt-0.5 h-4 w-4 shrink-0 rounded text-blue-600"
              />
              {question.trackSelectionOrder && otherSelected && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                  {selected.indexOf(OTHER_OPTION_ID) + 1}
                </span>
              )}
              <span className="min-w-0 flex-1 break-normal text-sm text-gray-700 [overflow-wrap:normal]">{stripHtml(otherLabel)}</span>
            </label>
            {otherSelected && (
              <input
                type="text"
                value={getOtherText(answer)}
                onChange={(event) => onChange(buildMultipleAnswer(selected, event.target.value))}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={stripHtml(otherPlaceholder)}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  if (question.type === 'dropdown') {
    const selectedValue = getSingleAnswerValue(answer)
    const otherSelected = selectedValue === OTHER_OPTION_ID

    return (
      <div className="space-y-2">
        <select
          value={selectedValue}
          onChange={(event) => onChange(buildSingleAnswer(event.target.value, getOtherText(answer)))}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{uiText('Selecione...', 'Select...', 'Seleccione...')}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {stripHtml(option.text)}
            </option>
          ))}
          {question.allowOther && (
            <option value={OTHER_OPTION_ID}>{stripHtml(otherLabel)}</option>
          )}
        </select>
        {otherSelected && (
          <input
            type="text"
            value={getOtherText(answer)}
            onChange={(event) => onChange(buildSingleAnswer(OTHER_OPTION_ID, event.target.value))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={stripHtml(otherPlaceholder)}
          />
        )}
      </div>
    )
  }

  if (question.type === 'rating') {
    const max = question.maxValue || 5
    const value = Number(answer) || 0
    const Icon = question.ratingIcon === 'heart'
      ? Heart
      : question.ratingIcon === 'thumb'
        ? ThumbsUp
        : question.ratingIcon === 'smile'
          ? Smile
          : Star
    const iconSize = question.ratingSize === 'large' ? 34 : question.ratingSize === 'small' ? 22 : 28
    const activeColor = question.ratingColor || '#facc15'

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <ScaleLabel label={question.minLabel} fallback="" />
          <ScaleLabel label={question.maxLabel} fallback="" />
        </div>
        <div className="flex gap-1">
        {Array.from({ length: max }, (_, index) => index + 1).map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="rounded p-1 text-gray-300 transition-colors"
            style={star <= value ? { color: activeColor } : undefined}
          >
            <Icon size={iconSize} fill={shouldFillRatingIcon(question.ratingIcon, star <= value) ? 'currentColor' : 'none'} strokeWidth={star <= value ? 2.5 : 2} />
          </button>
        ))}
        </div>
      </div>
    )
  }

  if (question.type === 'slider') {
    const min = question.minValue ?? 0
    const max = question.maxValue ?? 100
    const step = question.stepValue ?? 1
    const current = answer ?? min

    return (
      <div className="space-y-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={current}
          onChange={(event) => onChange(Number(event.target.value))}
          className="w-full accent-blue-600"
        />
        <div className="flex justify-between gap-3 text-xs text-gray-500">
          <ScaleLabel label={question.minLabel} fallback={min} className="break-normal text-left [overflow-wrap:normal]" />
          <span className="text-sm font-semibold text-blue-600">{current}</span>
          <ScaleLabel label={question.maxLabel} fallback={max} className="break-normal text-right [overflow-wrap:normal]" />
        </div>
      </div>
    )
  }

  if (question.type === 'rating_matrix') {
    const visibleRows = getRuntimeMatrixRows()
    const rows = (question.matrixRows || []).length > 0
      ? visibleRows
      : [{ id: 'row_1', label: uiText('Item 1', 'Item 1', 'Item 1') }]
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
    const max = question.maxValue || 5
    const Icon = question.ratingIcon === 'heart'
      ? Heart
      : question.ratingIcon === 'thumb'
        ? ThumbsUp
        : question.ratingIcon === 'smile'
          ? Smile
          : Star
    const iconSize = question.ratingSize === 'large' ? 30 : question.ratingSize === 'small' ? 20 : 25
    const activeColor = question.ratingColor || '#facc15'

    return (
      <div className="space-y-3">
        {rows.map((row) => {
          const rowValue = Number(currentValues[row.id]) || 0
          return (
            <div key={row.id} className="rounded-lg border border-gray-200 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="min-w-0 break-normal text-sm font-medium text-gray-700">{row.label}</span>
                <span className="shrink-0 text-xs text-gray-500">{rowValue || '-'}</span>
              </div>
              <div className="mb-2 flex items-center justify-between gap-3 text-xs text-gray-500">
                <ScaleLabel label={question.minLabel} fallback={1} className="break-normal text-left [overflow-wrap:normal]" />
                <ScaleLabel label={question.maxLabel} fallback={max} className="break-normal text-right [overflow-wrap:normal]" />
              </div>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: max }, (_, index) => index + 1).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onChange({ ...currentValues, [row.id]: value })}
                    className="rounded p-1 text-gray-300 transition-colors"
                    style={value <= rowValue ? { color: activeColor } : undefined}
                  >
                    <Icon size={iconSize} fill={shouldFillRatingIcon(question.ratingIcon, value <= rowValue) ? 'currentColor' : 'none'} strokeWidth={value <= rowValue ? 2.5 : 2} />
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (question.type === 'slider_matrix') {
    const visibleRows = getRuntimeMatrixRows()
    const unsortedRows = (question.matrixRows || []).length > 0
      ? visibleRows
      : [{ id: 'row_1', label: uiText('Item 1', 'Item 1', 'Item 1') }]
    const rows = question.sortMatrixRowsAlphabetically
      ? [...unsortedRows].sort((left, right) => left.label.localeCompare(right.label, language === 'pt' ? 'pt-BR' : language))
      : unsortedRows
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
    const min = question.minValue ?? 0
    const max = question.maxValue ?? 100
    const step = question.stepValue ?? 1

    return (
      <div className="space-y-4">
        {rows.map((row) => {
          const rowValue = currentValues[row.id] ?? min
          return (
            <div key={row.id} className="rounded-lg border border-gray-200 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="min-w-0 break-normal text-sm font-medium text-gray-700">{row.label}</span>
                <span className="shrink-0 text-sm font-semibold text-blue-600">{rowValue}</span>
              </div>
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={rowValue}
                onChange={(event) => onChange({ ...currentValues, [row.id]: Number(event.target.value) })}
                className="w-full accent-blue-600"
              />
              <div className="mt-1 flex justify-between gap-3 text-xs text-gray-500">
                <ScaleLabel label={question.minLabel} fallback={min} className="break-normal text-left" />
                <ScaleLabel label={question.maxLabel} fallback={max} className="break-normal text-right" />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (question.type === 'datetime') {
    const inputType =
      question.dateFormat === 'date' ? 'date' : question.dateFormat === 'time' ? 'time' : 'datetime-local'

    return (
      <input
        type={inputType}
        value={answer ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    )
  }

  if (question.type === 'media') {
    const watched = Boolean(answer)

    return (
      <div className="space-y-3">
        {question.videoUrl && (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-black">
            <video src={question.videoUrl} controls className="h-auto w-full" />
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
          <input
            type="checkbox"
            checked={watched}
            onChange={(event) => onChange(event.target.checked)}
            className="h-4 w-4 rounded text-blue-600"
          />
          <span className="text-sm text-gray-700">
            {uiText('Conteudo visualizado', 'Content viewed', 'Contenido visualizado')}
          </span>
        </label>
      </div>
    )
  }

  if (question.type === 'classification') {
    // Resposta ordenada (estilo Sphinx): clique define a posicao 1, 2, 3...
    const selected = Array.isArray(answer)
      ? answer.map(String)
      : (answer && typeof answer === 'object' && Array.isArray(answer.values))
        ? answer.values.map(String)
        : (answer && typeof answer === 'object' && Array.isArray(answer.orderedIds))
          ? answer.orderedIds.map(String)
          : []

    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-500">
          {uiText(
            'Clique nas opcoes na ordem desejada. A posicao aparece ao lado de cada resposta selecionada.',
            'Click the options in the desired order. The position appears next to each selected answer.',
            'Haga clic en las opciones en el orden deseado. La posicion aparece junto a cada respuesta seleccionada.'
          )}
        </p>
        {options.map((option) => {
          const isSelected = selected.includes(option.id)
          const position = isSelected ? selected.indexOf(option.id) + 1 : null
          return (
            <label key={option.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-2 hover:bg-gray-50">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(event) => {
                  if (event.target.checked) {
                    if (question.validation?.maxSelections && selected.length >= question.validation.maxSelections) {
                      toast.error(uiText(`Selecione no máximo ${question.validation.maxSelections} opção(ões).`, `Select at most ${question.validation.maxSelections} option(s).`, `Seleccione como máximo ${question.validation.maxSelections} opcion(es).`))
                      return
                    }
                    onChange([...selected.filter((id) => id !== option.id), option.id])
                    return
                  }
                  onChange(selected.filter((id) => id !== option.id))
                }}
                className="mt-0.5 h-4 w-4 shrink-0 rounded text-blue-600"
              />
              {position !== null && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                  {position}
                </span>
              )}
              <span className="survey-rich-text min-w-0 flex-1 break-normal text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: option.text }} />
            </label>
          )
        })}
      </div>
    )
  }

  if (question.type === 'ranking') {
    const currentOrder = Array.isArray(answer) && answer.length > 0
      ? answer.map(String).filter((id) => options.some((option) => option.id === id))
      : options.map((option) => option.id)
    const missingOptions = options.filter((option) => !currentOrder.includes(option.id)).map((option) => option.id)
    const orderedOptionIds = [...currentOrder, ...missingOptions]

    const moveItem = (fromIndex: number, toIndex: number) => {
      if (toIndex < 0 || toIndex >= orderedOptionIds.length) return
      const nextOrder = [...orderedOptionIds]
      const [removed] = nextOrder.splice(fromIndex, 1)
      nextOrder.splice(toIndex, 0, removed)
      onChange(nextOrder)
    }

    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-500">
          {uiText('Ordene os itens por preferência.', 'Order the items by preference.', 'Ordene los items por preferência.')}
        </p>
        {orderedOptionIds.map((optionId, index) => {
          const option = options.find((item) => item.id === optionId)
          if (!option) return null

          return (
            <div key={option.id} className="flex items-center gap-3 rounded-lg border border-gray-200 p-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
                {index + 1}
              </span>
              <span className="survey-rich-text min-w-0 flex-1 break-normal text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: option.text }} />
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(index, index - 1)}
                  disabled={index === 0}
                  className="rounded border border-gray-200 px-2 py-1 text-[0px] text-gray-600 after:text-xs after:content-['Subir'] disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, index + 1)}
                  disabled={index === orderedOptionIds.length - 1}
                  className="rounded border border-gray-200 px-2 py-1 text-[0px] text-gray-600 after:text-xs after:content-['Descer'] disabled:opacity-40"
                >
                  ↓
                </button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (question.type === 'matrix' || question.type === 'dropdown_matrix') {
    const visibleRows = getRuntimeMatrixRows()
    const visibleColumns = getRuntimeMatrixColumns()
    const rows = (question.matrixRows || []).length > 0
      ? visibleRows
      : [{ id: 'row_1', label: uiText('Item 1', 'Item 1', 'Item 1') }]
    const columns = (question.matrixColumns || []).length > 0
      ? visibleColumns
      : [
        { id: 'col_1', label: uiText('Opção 1', 'Option 1', 'Opcion 1'), value: '1' },
        { id: 'col_2', label: uiText('Opção 2', 'Option 2', 'Opcion 2'), value: '2' },
      ]
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}

    return (
      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="rounded-lg border border-gray-200 p-3">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {translatedTexts[`question:${question.id}:matrixRow:${row.id}`] || row.label}
            </label>
            <select
              value={currentValues[row.id] || ''}
              onChange={(event) => onChange({ ...currentValues, [row.id]: event.target.value })}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{uiText('Selecione...', 'Select...', 'Seleccione...')}</option>
              {columns.map((column) => (
                <option key={column.id} value={column.value}>
                  {translatedTexts[`question:${question.id}:matrixColumn:${column.id}`] || column.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    )
  }

  if (question.type === 'bestworst') {
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer)
      ? answer
      : { best: '', worst: '' }

    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {uiText('Melhor opção', 'Best option', 'Mejor opcion')}
          </label>
          <select
            value={currentValues.best || ''}
            onChange={(event) => onChange({ ...currentValues, best: event.target.value })}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{uiText('Selecione...', 'Select...', 'Seleccione...')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id} disabled={currentValues.worst === option.id}>
                {stripHtml(option.text)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {uiText('Pior opção', 'Worst option', 'Peor opcion')}
          </label>
          <select
            value={currentValues.worst || ''}
            onChange={(event) => onChange({ ...currentValues, worst: event.target.value })}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{uiText('Selecione...', 'Select...', 'Seleccione...')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id} disabled={currentValues.best === option.id}>
                {stripHtml(option.text)}
              </option>
            ))}
          </select>
        </div>
      </div>
    )
  }

  if (question.type === 'multitext') {
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
    const visibleTextBoxes = getVisibleTextBoxes(question, questions, answers)
    const textBoxes = (question.textBoxes || []).length > 0 ? visibleTextBoxes : []
    const numericTotal = textBoxes.reduce((total, box) => {
      if (box.type !== 'number') return total
      const value = Number(currentValues[box.id] || 0)
      return total + (Number.isFinite(value) ? value : 0)
    }, 0)

    return (
      <div className="space-y-3">
        {textBoxes.map((box) => (
          <div key={box.id}>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {resolveDynamicText(translatedTexts[`question:${question.id}:textBox:${box.id}:label`] || box.label, questions, answers)}
              {box.required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
              type={box.type}
              value={currentValues[box.id] || ''}
              onChange={(event) => onChange({ ...currentValues, [box.id]: event.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={translatedTexts[`question:${question.id}:textBox:${box.id}:placeholder`] || box.placeholder || ''}
            />
          </div>
        ))}
        {question.sumValidation?.enabled && (
          <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800">
            {uiText(
              numericTotal === Number(question.sumValidation.targetSum)
                ? `Soma atual: ${numericTotal}. Total correto.`
                : numericTotal < Number(question.sumValidation.targetSum)
                  ? `Soma atual: ${numericTotal}. Faltam ${Number(question.sumValidation.targetSum) - numericTotal}.`
                  : `Soma atual: ${numericTotal}. Excedeu ${numericTotal - Number(question.sumValidation.targetSum)}.`,
              `Current sum: ${numericTotal}. Target: ${question.sumValidation.targetSum}.`,
              `Suma actual: ${numericTotal}. Meta: ${question.sumValidation.targetSum}.`
            )}
          </p>
        )}
      </div>
    )
  }

  return (
    <input
      type="text"
      value={answer ?? ''}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder={uiText('Digite sua resposta...', 'Type your answer...', 'Escriba su respuesta...')}
    />
  )
}

const SURVEY_VALIDATION_TOAST_ID = 'dna-survey-validation-toast'

const ResearcherSurveyModal: React.FC<Props> = ({
  survey,
  researcherId = 'public_link',
  researcherName = '',
  testMode = false,
  isPublicLink = false,
  onClose,
  onCancelled,
  onSubmitted,
}) => {
  const surveyId = survey?._id || (survey as any)?.id || ''
  const orderedQuestions = useMemo(() => buildOrdered(survey), [survey])
  const indexMap = useMemo(() => buildIndexMap(orderedQuestions), [orderedQuestions])
  const pages = survey.pages || []
  const surveyLogoUrl = resolveSurveyLogoUrl(survey)
  // O consentimento de gravacao so aparece quando a pesquisa tem
  // "Esta pesquisa tera gravacao de audio" marcado; caso contrario, a coleta
  // abre direto na introducao, sem perguntar ao participante (inclui pesquisas
  // antigas salvas com audioRecordingEnabled: false).
  const audioRecordingEnabled = survey.audioRecordingEnabled === true
  const availableLanguages = useMemo<SurveyLanguage[]>(() => SURVEY_APPLICATION_LANGUAGES, [])
  const draftKey = isPublicLink
    ? `public_survey_draft:${surveyId}`
    : testMode
      ? getDraftKey(surveyId, `test:${researcherId}`)
      : getDraftKey(surveyId, researcherId)
  const pendingKey = isPublicLink || testMode
    ? `dna_pending_submission:public:${surveyId}`
    : getPendingKey(surveyId, researcherId || 'unknown')

  const [answers, setAnswers] = useState<Answers>({})
  const [curIdx, setCurIdx] = useState<number>(() => 0)
  const currentQuestion = orderedQuestions[curIdx]
  const [navHistory, setNavHistory] = useState<number[]>([])
  const [terminalPage, setTerminalPage] = useState<SurveyPage | null>(null)
  const [introCompleted, setIntroCompleted] = useState(false)
  const [visitedQuestionIds, setVisitedQuestionIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [language, setLanguage] = useState<SurveyLanguage>(availableLanguages[0] || 'pt')
  const [translatedTexts, setTranslatedTexts] = useState<Record<string, string>>({})
  const [translationLoading, setTranslationLoading] = useState(false)
  const translationRunRef = useRef(0)
  const surveyTitleHtml = translatedTexts['survey:title'] || autoTranslateHtml(ensureHtmlString(survey.title), language)
  const surveyDescriptionHtml = translatedTexts['survey:description'] || autoTranslateHtml(ensureHtmlString(survey.description), language)
  const uiText = (pt: string, en: string, es: string) => normalizeDisplayText(getUiText(language, pt, en, es))

  const [audioDecisionMade, setAudioDecisionMade] = useState(!audioRecordingEnabled)
  const [audioConsent, setAudioConsent] = useState(false)
  const [audioConsentAt, setAudioConsentAt] = useState<string | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [audioRecordedAt, setAudioRecordedAt] = useState<string | null>(null)
  const [submittedResult, setSubmittedResult] = useState<SubmittedResult | null>(null)
  const [validationAlertQuestionId, setValidationAlertQuestionId] = useState<string | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const recordingIntervalRef = useRef<number | null>(null)
  const recordingSecondsRef = useRef(0)
  const suppressDraftSaveRef = useRef(false)
  const initializedSurveyIdRef = useRef<string | null>(null)
  const [pendingOffline, setPendingOffline] = useState(false)

  // ── Inicialização única por survey + restauração do rascunho ──────────────
  useEffect(() => {
    if (initializedSurveyIdRef.current === surveyId) return
    initializedSurveyIdRef.current = surveyId

    let restoredFromDraft = false
    try {
      const rawDraft = window.localStorage.getItem(draftKey)
      if (rawDraft) {
        const draft = JSON.parse(rawDraft) as SurveyDraft
        if (!draft.terminalPageId) {
          if (draft.answers && Object.keys(draft.answers).length > 0) {
            setAnswers(draft.answers)
            restoredFromDraft = true
          }
          if (typeof draft.curIdx === 'number') setCurIdx(draft.curIdx)
          if (typeof draft.introCompleted === 'boolean') {
            setIntroCompleted(draft.introCompleted)
          } else if (typeof draft.audioDecisionMade === 'boolean') {
            setIntroCompleted(draft.audioDecisionMade)
          }
          if (Array.isArray(draft.visitedQuestionIds)) setVisitedQuestionIds(draft.visitedQuestionIds.map(String))
          if (audioRecordingEnabled) {
            if (typeof draft.audioDecisionMade === 'boolean') setAudioDecisionMade(draft.audioDecisionMade)
            if (typeof draft.audioConsent === 'boolean') setAudioConsent(draft.audioConsent)
            if (draft.audioConsentAt) setAudioConsentAt(draft.audioConsentAt)
          } else {
            setAudioDecisionMade(true)
            setAudioConsent(false)
            setAudioConsentAt(null)
          }
          if (draft.language && availableLanguages.includes(draft.language)) setLanguage(draft.language)
          if (draft.terminalPageId) {
            const page = pages.find((item) => item.id === draft.terminalPageId) || null
            setTerminalPage(page)
          }
        }
      }
    } catch {
      // ignore invalid draft
    }

    if (!restoredFromDraft) {
      const firstVisibleIndex = orderedQuestions.findIndex((question) =>
        evaluateDisplayConditions(question, orderedQuestions, {})
      )
      setCurIdx(firstVisibleIndex >= 0 ? firstVisibleIndex : 0)
      setTerminalPage(null)
      setIntroCompleted(false)
      setVisitedQuestionIds([])
      setAudioDecisionMade(!audioRecordingEnabled)
      setAudioConsent(false)
      setAudioConsentAt(null)
      setLanguage((current) => (availableLanguages.includes(current) ? current : availableLanguages[0] || 'pt'))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId])

  // Perguntas de data/hora iniciam preenchidas com o momento em que a resposta
  // chega na pergunta. Aplicado so quando a pergunta e exibida (perguntas
  // desviadas/ocultas nao sao preenchidas) e respeita rascunhos ja salvos.
  useEffect(() => {
    if (!currentQuestion || currentQuestion.type !== 'datetime') return
    const existing = answers[currentQuestion.id]
    if (existing !== undefined && existing !== '') return
    const now = new Date()
    const pad = (value: number) => String(value).padStart(2, '0')
    const datePart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    const timePart = `${pad(now.getHours())}:${pad(now.getMinutes())}`
    const prefilled = currentQuestion.dateFormat === 'date'
      ? datePart
      : currentQuestion.dateFormat === 'time'
        ? timePart
        : `${datePart}T${timePart}`
    setAnswers((previous) => ({
      ...previous,
      [currentQuestion.id]: previous[currentQuestion.id] ?? prefilled,
    }))
  }, [curIdx, currentQuestion, answers])

  useEffect(() => {
    translationRunRef.current += 1
    const runId = translationRunRef.current

    if (language === 'pt') {
      setTranslatedTexts({})
      setTranslationLoading(false)
      return
    }

    const items = buildTranslationItems(survey, orderedQuestions, pages)
    setTranslatedTexts({})
    setTranslationLoading(items.length > 0)

    Promise.all(items.map(async (item) => ({
      key: item.key,
      value: await machineTranslateHtml(item.value, language),
    }))).then((translatedItems) => {
      if (translationRunRef.current !== runId) return

      setTranslatedTexts(Object.fromEntries(translatedItems.map((item) => [item.key, item.value])))
      setTranslationLoading(false)
    }).catch((error) => {
      if (translationRunRef.current !== runId) return
      console.warn('Não foi possível traduzir a pesquisa completa:', error)
      setTranslationLoading(false)
    })
  }, [language, orderedQuestions, pages, survey])

  useEffect(() => {
    if (suppressDraftSaveRef.current || submittedResult) return

    const draft: SurveyDraft = {
      answers,
      curIdx,
      terminalPageId: terminalPage?.id || null,
      introCompleted,
      visitedQuestionIds,
      audioDecisionMade,
      audioConsent,
      audioConsentAt,
      language,
    }

    try {
      window.localStorage.setItem(draftKey, JSON.stringify(draft))
    } catch {
      // ignore storage errors
    }
  }, [answers, audioConsent, audioConsentAt, audioDecisionMade, curIdx, draftKey, introCompleted, language, submittedResult, terminalPage, visitedQuestionIds])


  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [offlineCount, setOfflineCount] = useState(() => getOfflineQueueCount({ surveyId }))
  const [isSyncingOffline, setIsSyncingOffline] = useState(false)

  const updateOfflineCount = () => {
    setOfflineCount(getOfflineQueueCount({ surveyId }))
  }

  // ── Listener de fila offline e sincronização ao reconectar ─────────────────
  useEffect(() => {
    updateOfflineCount()

    const handleOnlineStatus = async () => {
      setIsOffline(false)
      updateOfflineCount()
      const pending = getOfflineQueueCount({ surveyId: survey._id })
      if (pending > 0 && !testMode) {
        setIsSyncingOffline(true)
        try {
          const res = await syncOfflineSubmissions({ surveyId: survey._id })
          if (res.sent > 0) {
            toast.success(uiText(
              `${res.sent} resposta(s) offline sincronizada(s) com sucesso!`,
              `${res.sent} offline response(s) synced successfully!`,
              `¡${res.sent} respuesta(s) offline sincronizada(s) con exito!`
            ))
          }
        } catch {
          // ignore
        } finally {
          setIsSyncingOffline(false)
          updateOfflineCount()
        }
      }
    }

    const handleOfflineStatus = () => {
      setIsOffline(true)
      updateOfflineCount()
    }

    const handleQueueChange = () => {
      updateOfflineCount()
    }

    window.addEventListener('online', handleOnlineStatus)
    window.addEventListener('offline', handleOfflineStatus)
    window.addEventListener('dna_offline_queue_changed', handleQueueChange)

    return () => {
      window.removeEventListener('online', handleOnlineStatus)
      window.removeEventListener('offline', handleOfflineStatus)
      window.removeEventListener('dna_offline_queue_changed', handleQueueChange)
    }
  }, [surveyId, testMode, language])
  // ─────────────────────────────────────────────────────────────────────────

  const clearDraft = () => {
    suppressDraftSaveRef.current = true
    try {
      window.localStorage.removeItem(draftKey)
    } catch {
      // ignore
    }
  }

  useEffect(() => () => {
    const audioUrl = submittedResult?.audio?.audioUrl
    if (audioUrl?.startsWith('blob:')) {
      try { URL.revokeObjectURL(audioUrl) } catch { /* ignore */ }
    }
  }, [submittedResult])

  const clearRecordingInterval = () => {
    if (recordingIntervalRef.current !== null) {
      window.clearInterval(recordingIntervalRef.current)
      recordingIntervalRef.current = null
    }
  }

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  const discardAudioRecording = () => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null
      recorder.onstop = null
      try { recorder.stop() } catch { /* ignore */ }
    }

    clearRecordingInterval()
    stopStream()
    chunksRef.current = []
    recorderRef.current = null
    recordingSecondsRef.current = 0
    setRecordingSeconds(0)
    setAudioRecordedAt(null)
    setIsRecording(false)
    setIsUploading(false)
  }

  const resetAudioChoice = () => {
    discardAudioRecording()
    setAudioDecisionMade(!audioRecordingEnabled)
    setAudioConsent(false)
    setAudioConsentAt(null)
  }

  const resetSurveyProgress = () => {
    setAnswers({})
    setCurIdx(0)
    setNavHistory([])
    setTerminalPage(null)
    setIntroCompleted(false)
    setVisitedQuestionIds([])
    setValidationAlertQuestionId(null)
    toast.dismiss(SURVEY_VALIDATION_TOAST_ID)
  }

  const restartNewInterview = () => {
    clearDraft()
    resetSurveyProgress()
    resetAudioChoice()
    setSubmittedResult(null)
    setValidationAlertQuestionId(null)
    toast.dismiss(SURVEY_VALIDATION_TOAST_ID)
  }

  const chooseWithoutAudio = () => {
    discardAudioRecording()
    setAudioDecisionMade(true)
    setAudioConsent(false)
    setAudioConsentAt(null)
  }

  const buildResponseAnswers = () => (
    Object.entries(answers)
      .filter(([, answer]) => isAnswerFilled(answer))
      .map(([questionId, answer]) => {
        const question = orderedQuestions.find((item) => item.id === questionId)
        const resolvedContent = question
          ? getResolvedQuestionContent(question, orderedQuestions, answers)
          : null
        const localizedContent = question && resolvedContent
          ? getLocalizedQuestionContent(question, language, resolvedContent.title, resolvedContent.description)
          : null

        return {
          questionId,
          questionTitle: question
            ? translatedTexts[`question:${question.id}:title`] || localizedContent?.title || question.title || ''
            : localizedContent?.title || '',
          questionType: question?.type || '',
          answer,
        }
      })
  )

  const handleCancelSurvey = async () => {
    if (isUploading || submitting) {
      toast.error(uiText('Aguarde a finalização antes de cancelar.', 'Wait for completion before cancelling.', 'Espere a que finalice antes de cancelar.'))
      return
    }

    const answeredItems = buildResponseAnswers()
    const hasProgress = answeredItems.length > 0
    const confirmed = window.confirm(uiText(
      hasProgress
        ? 'Cancelar esta entrevista? As respostas já preenchidas serao salvas e uma nova entrevista podera ser iniciada.'
        : 'Cancelar esta entrevista e voltar para o inicio?',
      hasProgress
        ? 'Cancel this interview? The answers already filled in will be saved and a new interview can be started.'
        : 'Cancel this interview and return to the start?',
      hasProgress
        ? 'Cancelar esta entrevista? Las respuestas ya completadas se guardaran y se podra iniciar una nueva entrevista.'
        : 'Cancelar esta entrevista y volver al inicio?'
    ))
    if (!confirmed) return

    if (testMode) {
      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      toast.success(uiText('Teste cancelado.', 'Test cancelled.', 'Prueba cancelada.'))
      onClose?.()
      return
    }

    discardAudioRecording()

    if (!hasProgress) {
      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      if (isPublicLink && onCancelled) {
        onCancelled(false)
      } else {
        onClose?.()
      }
      return
    }

    setSubmitting(true)
    const cancelledAt = new Date().toISOString()

    if (!navigator.onLine) {
      const offlineId = `off_cancel_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      const submission: OfflineSubmission = {
        id: offlineId,
        surveyId: survey._id,
        surveyTitle: survey.title,
        researcherId: researcherId || 'public_link',
        researcherName: researcherName || '',
        language,
        answers: answeredItems,
        completedAt: cancelledAt,
        startedAt: cancelledAt,
        savedAt: cancelledAt,
        isCancelled: true,
        cancellationReason: 'Entrevista cancelada com progresso',
      }
      addOfflineSubmission(submission)
      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      updateOfflineCount()
      toast.success(uiText(
        'Entrevista cancelada. As respostas preenchidas foram salvas na fila offline.',
        'Interview cancelled. The filled answers were saved to offline queue.',
        'Entrevista cancelada. Las respuestas completadas fueron guardadas en la cola offline.'
      ))
      setSubmitting(false)
      if (isPublicLink && onCancelled) {
        onCancelled(true)
      } else {
        onSubmitted()
      }
      return
    }

    try {
      await lumi.entities.responses.create({
        surveyId: survey._id,
        surveyTitle: survey.title,
        researcherId,
        researcherName: researcherName || '',
        language,
        status: 'cancelled',
        cancelledAt,
        createdAt: cancelledAt,
        updatedAt: cancelledAt,
        audio_consent: false,
        answers: answeredItems,
      })

      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      toast.success(uiText(
        'Entrevista cancelada. As respostas preenchidas foram salvas.',
        'Interview cancelled. The filled answers were saved.',
        'Entrevista cancelada. Las respuestas completadas fueron guardadas.'
      ))
      if (isPublicLink && onCancelled) {
        onCancelled(true)
      } else {
        onSubmitted()
      }
    } catch (error) {
      console.error('Erro ao salvar cancelamento da entrevista:', error)
      if (isNetworkError(error)) {
        const offlineId = `off_cancel_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        const submission: OfflineSubmission = {
          id: offlineId,
          surveyId: survey._id,
          surveyTitle: survey.title,
          researcherId: researcherId || 'public_link',
          researcherName: researcherName || '',
          language,
          answers: answeredItems,
          completedAt: cancelledAt,
          startedAt: cancelledAt,
          savedAt: cancelledAt,
          isCancelled: true,
          cancellationReason: 'Entrevista cancelada com progresso',
        }
        addOfflineSubmission(submission)
        clearDraft()
        resetSurveyProgress()
        resetAudioChoice()
        updateOfflineCount()
        toast.success(uiText(
          'Entrevista cancelada. As respostas foram salvas na fila offline.',
          'Interview cancelled. The answers were saved to offline queue.',
          'Entrevista cancelada. Las respuestas fueron guardadas en la cola offline.'
        ))
        if (isPublicLink && onCancelled) {
          onCancelled(true)
        } else {
          onSubmitted()
        }
      } else {
        toast.error(uiText(
          'Não foi possível salvar o cancelamento. Tente novamente.',
          'Could not save the cancellation. Try again.',
          'No fue posible guardar la cancelacion. Intente nuevamente.'
        ))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isUploading || submitting) {
      toast.error(uiText('Aguarde a finalização antes de fechar.', 'Wait for completion before closing.', 'Espere a que finalice antes de cerrar.'))
      return
    }

    const hasDraft = Object.keys(answers).length > 0 || audioDecisionMade
    if (hasDraft) {
      const confirmed = window.confirm(uiText(
        'Fechar agora vai encerrar esta resposta em andamento. Deseja continuar?',
        'Closing now will end this response in progress. Do you want to continue?',
        'Cerrar ahora finalizara esta respuesta en curso. Desea continuar?'
      ))
      if (!confirmed) return
    }

    clearDraft()
    resetAudioChoice()
    onClose?.()
  }

  const startRecording = async () => {
    if (isRecording || isUploading) return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mime = getSupportedMime()
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)

      chunksRef.current = []
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }

      recorderRef.current = recorder
      recorder.start(500)
      const startedAt = new Date().toISOString()
      setAudioDecisionMade(true)
      setAudioConsent(true)
      setAudioRecordedAt(startedAt)
      if (!audioConsentAt) setAudioConsentAt(startedAt)
      recordingSecondsRef.current = 0
      setRecordingSeconds(0)
      clearRecordingInterval()
      recordingIntervalRef.current = window.setInterval(() => {
        recordingSecondsRef.current += 1
        setRecordingSeconds(recordingSecondsRef.current)
      }, 1000)
      setIsRecording(true)
    } catch (micErr) {
      console.warn('Microfone indisponível ou permissão negada, continuando sem áudio:', micErr)
      toast.error(uiText(
        'Microfone indisponível. A entrevista iniciará sem gravação de áudio.',
        'Microphone unavailable. The interview will start without audio recording.',
        'Microfono no disponible. La entrevista comenzara sin grabacion de audio.'
      ), { duration: 4000 })
      chooseWithoutAudio()
    }
  }

  const finishRecordingForResponse = async (responseId: string) => {
    const recorder = recorderRef.current
    if (!audioConsent || !recorder || recorder.state === 'inactive') return null

    setIsUploading(true)

    try {
      const mimeType = recorder.mimeType || getSupportedMime() || 'audio/webm'
      const durationSec = recordingSecondsRef.current
      const recordedAt = audioRecordedAt || new Date().toISOString()

      const blob = await new Promise<Blob>((resolve, reject) => {
        recorder.onstop = () => {
          try {
            resolve(new Blob(chunksRef.current, { type: mimeType }))
          } catch (error) {
            reject(error)
          }
        }

        recorder.onerror = () => reject(new Error('Falha ao finalizar o áudio.'))

        try {
          recorder.stop()
        } catch (error) {
          reject(error)
        }
      })

      const mp3Blob = await convertRecordedAudioToMp3(blob)
      const fileName = buildAudioFileName(survey._id, responseId, 'audio/mpeg')
      const file = new File([mp3Blob], fileName, { type: 'audio/mpeg' })
      const result = await lumi.tools.file.upload([file])
      const fileUrl = result?.[0]?.fileUrl

      if (!fileUrl) {
        throw new Error(result?.[0]?.uploadError || 'Falha ao salvar áudio.')
      }

      const audioFile = new File([mp3Blob], fileName, { type: 'audio/mpeg' })
      const previewUrl = URL.createObjectURL(mp3Blob)

      return {
        audio_url: fileUrl,
        audio_nome: fileName,
        data_gravação: recordedAt,
        duração_audio: durationSec,
        audioBlob: mp3Blob,
        audioFile,
        audioPreviewUrl: previewUrl,
      }
    } finally {
      clearRecordingInterval()
      stopStream()
      chunksRef.current = []
      recorderRef.current = null
      recordingSecondsRef.current = 0
      setRecordingSeconds(0)
      setIsRecording(false)
      setIsUploading(false)
    }
  }

  const resolvedCurrentQuestionContent = currentQuestion
    ? getResolvedQuestionContent(currentQuestion, orderedQuestions, answers)
    : null
  const currentQuestionContent = currentQuestion && resolvedCurrentQuestionContent
    ? getLocalizedQuestionContent(
      currentQuestion,
      language,
      resolvedCurrentQuestionContent.title,
      resolvedCurrentQuestionContent.description
    )
    : null
  const currentQuestionTitleHtml = ensureHtmlString(currentQuestionContent?.title || currentQuestion?.title)
  const currentQuestionDescriptionHtml = ensureHtmlString(currentQuestionContent?.description || currentQuestion?.description)
  const translatedCurrentQuestionTitleHtml = currentQuestion
    ? translatedTexts[`question:${currentQuestion.id}:title`] || currentQuestionTitleHtml
    : currentQuestionTitleHtml
  const translatedCurrentQuestionDescriptionHtml = currentQuestion
    ? translatedTexts[`question:${currentQuestion.id}:description`] || currentQuestionDescriptionHtml
    : currentQuestionDescriptionHtml
  const currentQuestionImageUrl = typeof currentQuestion?.imageUrl === 'string'
    ? currentQuestion.imageUrl.trim()
    : ''
  const nextLocation = terminalPage || !currentQuestion
    ? null
    : getNextSurveyLocation(curIdx, orderedQuestions, indexMap, answers, pages)
  const showIntroduction = audioDecisionMade && !introCompleted && !terminalPage
  const hasMoreQuestionsAhead = orderedQuestions.slice(curIdx + 1).some((q) => evaluateDisplayConditions(q, orderedQuestions, answers))
  const isFinalStep = showIntroduction ? false : (!!terminalPage || nextLocation === null || !hasMoreQuestionsAhead)
  const isFirstStep = showIntroduction
  const progress = terminalPage
    ? 100
    : showIntroduction
      ? 0
    : orderedQuestions.length > 0
      ? Math.round(((curIdx + 1) / orderedQuestions.length) * 100)
      : 0

  useEffect(() => {
    if (!audioDecisionMade || !introCompleted || terminalPage || !currentQuestion?.id) return

    setVisitedQuestionIds((previous) => (
      previous.includes(currentQuestion.id) ? previous : [...previous, currentQuestion.id]
    ))
  }, [audioDecisionMade, currentQuestion?.id, introCompleted, terminalPage])
  const terminalPageTitleHtml = terminalPage
    ? translatedTexts[`page:${terminalPage.id}:title`] || autoTranslateHtml(ensureHtmlString(terminalPage.title || 'Pesquisa concluída'), language)
    : ''
  const terminalPageDescriptionHtml = terminalPage
    ? translatedTexts[`page:${terminalPage.id}:description`] || autoTranslateHtml(ensureHtmlString(terminalPage.description), language)
    : ''
  const currentPage = currentQuestion
    ? pages.find((page) => (page.questionIds || []).includes(currentQuestion.id)) || null
    : null
  const currentPageTitleHtml = currentPage
    ? translatedTexts[`page:${currentPage.id}:title`] || autoTranslateHtml(ensureHtmlString(currentPage.title), language)
    : ''
  const currentPageDescriptionHtml = currentPage
    ? translatedTexts[`page:${currentPage.id}:description`] || autoTranslateHtml(ensureHtmlString(currentPage.description), language)
    : ''
  const shouldShowCurrentPageTitle = Boolean(currentPage && survey.showPageTitles !== false && currentPage.showTitle !== false)
  const shouldShowLogoInCurrentScreen = Boolean(surveyLogoUrl && survey.showLogoOnEveryPage !== false && !showIntroduction)

  const closeSubmittedResult = () => {
    clearDraft()
    resetSurveyProgress()
    resetAudioChoice()
    onSubmitted()
  }

  const triggerAudioDownload = (url: string, fileName: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    window.setTimeout(() => {
      document.body.removeChild(link)
    }, 1200)
  }

  const downloadSubmittedAudio = async () => {
    const audio = submittedResult?.audio
    if (!audio) return
    try {
      await downloadAudioDirectly(
        audio.blob || audio.file || audio.audioUrl || audio.remoteAudioUrl,
        audio.audioName,
        {
          responseId: audio.responseId || submittedResult?.responseId,
          audioData: audio.audioUrl?.startsWith('data:') ? audio.audioUrl : undefined,
        }
      )
      toast.success(uiText('Download do áudio (.mp3) iniciado.', 'Audio download (.mp3) started.', 'Descarga del audio (.mp3) iniciada.'))
    } catch (err) {
      console.error('Erro ao baixar áudio:', err)
      toast.error(uiText('Não foi possível baixar o áudio.', 'Could not download audio.', 'No se pudo descargar el audio.'))
    }
  }

  const shareSubmittedAudio = async () => {
    const audio = submittedResult?.audio
    if (!audio) return
    const shareText = uiText('Áudio da entrevista concluída.', 'Audio from the completed interview.', 'Audio de la entrevista concluida.')
    const shareUrl = audio.remoteAudioUrl || audio.audioUrl
    const isAbortError = (error: unknown) => (error as Error)?.name === 'AbortError'

    if (navigator.canShare?.({ files: [audio.file] })) {
      try {
        await navigator.share({
          title: audio.audioName,
          text: shareText,
          files: [audio.file],
        })
        return
      } catch (error) {
        if (isAbortError(error)) return
        console.warn('Compartilhamento do arquivo de áudio recusado, tentando link:', error)
      }
    }

    if (navigator.share && shareUrl.startsWith('http')) {
      try {
        await navigator.share({
          title: audio.audioName,
          text: shareText,
          url: shareUrl,
        })
        return
      } catch (error) {
        if (isAbortError(error)) return
        console.warn('Compartilhamento do link de áudio recusado, tentando fallback:', error)
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success(uiText(
        'Link do áudio copiado. Abra o WhatsApp e cole no grupo.',
        'Audio link copied. Open WhatsApp and paste it in the group.',
        'Enlace del audio copiado. Abra WhatsApp y peguelo en el grupo.'
      ))
    } catch (error) {
      console.error('Erro ao compartilhar audio:', error)
      toast.error(uiText(
        'Não foi possível compartilhar automaticamente. Use Baixar áudio e envie o arquivo pelo WhatsApp.',
        'Could not share automatically. Use Download audio and send the file through WhatsApp.',
        'No fue posible compartir automaticamente. Use Descargar audio y envie el archivo por WhatsApp.'
      ))
    }
  }

  const getSelectionValidationError = (question: Question, answer: any) => {
    if (!['multiple', 'checkbox'].includes(question.type)) return null

    const selected = getAnswerValues(answer)
    const minSelections = question.validation?.minSelections
    const maxSelections = question.validation?.maxSelections

    if (typeof minSelections === 'number' && selected.length < minSelections) {
      return uiText(
        `Selecione pelo menos ${minSelections} opção(oes).`,
        `Select at least ${minSelections} option(s).`,
        `Seleccione al menos ${minSelections} opcion(es).`
      )
    }

    if (typeof maxSelections === 'number' && selected.length > maxSelections) {
      return uiText(
        `Selecione no máximo ${maxSelections} opção(oes).`,
        `Select at most ${maxSelections} option(s).`,
        `Seleccione como máximo ${maxSelections} opcion(es).`
      )
    }

    return null
  }

  const getQuestionValidationError = (question: Question, answer: any) => {
    const values = getAnswerValues(answer)

    if (question.allowOther && question.requireOtherText !== false && values.includes(OTHER_OPTION_ID) && !getOtherText(answer).trim()) {
      return uiText('Digite a resposta no campo Outro.', 'Type the answer in the Other field.', 'Escriba la respuesta en el campo Otro.')
    }

    // O somatório tem prioridade sobre a regra genérica de obrigatoriedade.
    // Assim, quando todos os campos estiverem preenchidos mas o total estiver incorreto,
    // o pesquisador recebe a orientação que explica exatamente o que precisa ajustar.
    if (question.type === 'multitext' && question.sumValidation?.enabled) {
      const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
      const numericTotal = getVisibleTextBoxes(question, orderedQuestions, answers).reduce((total, box) => {
        if (box.type !== 'number') return total
        const value = Number(currentValues[box.id] || 0)
        return total + (Number.isFinite(value) ? value : 0)
      }, 0)
      const targetSum = Number(question.sumValidation.targetSum)

      if (numericTotal !== targetSum) {
        return uiText(
          numericTotal < targetSum
            ? `A soma atual é ${numericTotal}, mas o total deve ser ${targetSum}. Faltam ${targetSum - numericTotal}.`
            : `A soma atual é ${numericTotal}, mas o total deve ser ${targetSum}. Excedeu ${numericTotal - targetSum}.`,
          `The current sum is ${numericTotal}, but the total must be ${targetSum}.`,
          `La suma actual es ${numericTotal}, pero el total debe ser ${targetSum}.`
        )
      }
    }

    if (question.required && !isQuestionAnswerComplete(question, answer, orderedQuestions, answers)) {
      if (question.type === 'multitext') {
        const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
        const missingBox = getVisibleTextBoxes(question, orderedQuestions, answers).find((box) => box.required && !isAnswerFilled(currentValues[box.id]))
        if (missingBox) {
          return uiText('Preencha todos os campos obrigatórios.', 'Fill in all required fields.', 'Complete todos los campos obligatorios.')
        }
      }

      if (['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type)) {
        return uiText('Responda todos os itens desta pergunta.', 'Answer every item in this question.', 'Responda todos los items de esta pregunta.')
      }

      if (question.type === 'classification') {
        return uiText('Selecione ao menos uma opcao na ordem desejada.', 'Select at least one option in the desired order.', 'Seleccione al menos una opcion en el orden deseado.')
      }

      if (question.type === 'bestworst') {
        return uiText('Selecione a melhor e a pior opção.', 'Select the best and worst option.', 'Seleccione la mejor y la peor opcion.')
      }

      return uiText('Esta pergunta é obrigatória.', 'This question is required.', 'Esta pregunta es obligatoria.')
    }

    return getSelectionValidationError(question, answer)
  }

  const handleAnswer = (questionId: string, value: any) => {
    setAnswers((previous) => {
      return { ...previous, [questionId]: value }
    })
    toast.dismiss(SURVEY_VALIDATION_TOAST_ID)
    if (validationAlertQuestionId === questionId) {
      setValidationAlertQuestionId(null)
    }
  }

  const handleNext = () => {
    if (showIntroduction) {
      setIntroCompleted(true)
      setTerminalPage(null)
      setValidationAlertQuestionId(null)
      toast.dismiss(SURVEY_VALIDATION_TOAST_ID)
      return
    }

    if (!currentQuestion) return
    const questionError = getQuestionValidationError(currentQuestion, answers[currentQuestion.id])
    if (questionError) {
      setValidationAlertQuestionId(currentQuestion.id)
      toast.error(questionError, { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      return
    }
    if (currentQuestion.required && !isQuestionAnswerComplete(currentQuestion, answers[currentQuestion.id], orderedQuestions, answers)) {
      setValidationAlertQuestionId(currentQuestion.id)
      toast.error(uiText('Esta pergunta é obrigatória.', 'This question is required.', 'Esta pregunta es obligatoria.'), { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      return
    }

    const selectionError = getSelectionValidationError(currentQuestion, answers[currentQuestion.id])
    if (selectionError) {
      setValidationAlertQuestionId(currentQuestion.id)
      toast.error(selectionError, { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      return
    }

    setValidationAlertQuestionId(null)
    toast.dismiss(SURVEY_VALIDATION_TOAST_ID)

    const resolvedNext = getNextSurveyLocation(curIdx, orderedQuestions, indexMap, answers, pages)
    if (!resolvedNext) return

    if (resolvedNext.kind === 'terminal') {
      setNavHistory((prev) => [...prev, curIdx])
      setTerminalPage(resolvedNext.page)
      return
    }

    setNavHistory((prev) => [...prev, curIdx])
    setTerminalPage(null)
    setCurIdx(resolvedNext.index)
  }

  const handlePrev = () => {
    setValidationAlertQuestionId(null)
    toast.dismiss(SURVEY_VALIDATION_TOAST_ID)
    if (terminalPage) {
      // Sair da pagina terminal: volta para o indice que levou ao terminal
      setTerminalPage(null)
      if (navHistory.length > 0) {
        const last = navHistory[navHistory.length - 1]
        setCurIdx(last)
        setNavHistory(navHistory.slice(0, -1))
      }
      return
    }

    if (!introCompleted) {
      return
    }

    // Voltar: desempilhar historico de navegacao (respeita desvios condicionais)
    if (navHistory.length > 0) {
      const last = navHistory[navHistory.length - 1]
      setCurIdx(last)
      setNavHistory(navHistory.slice(0, -1))
      return
    }

    // Historico vazio: fallback linear (inicio da pesquisa ou pesquisa sem desvios)
    const prevIndex = getPreviousVisibleQuestionIndex(curIdx, orderedQuestions, answers, pages)
    if (prevIndex !== null) {
      setCurIdx(prevIndex)
      return
    }

    setIntroCompleted(false)
  }

  const getActivePathQuestions = () => {
    const activeQuestionIds = new Set<string>()
    const visitedIndexes = new Set<number>()
    let index = orderedQuestions.findIndex((question) =>
      evaluateDisplayConditions(question, orderedQuestions, answers)
    )

    while (index >= 0 && index < orderedQuestions.length && !visitedIndexes.has(index)) {
      const question = orderedQuestions[index]
      if (!question) break

      visitedIndexes.add(index)
      activeQuestionIds.add(question.id)

      const nextLocation = getNextSurveyLocation(index, orderedQuestions, indexMap, answers, pages)
      if (!nextLocation || nextLocation.kind === 'terminal') break

      index = nextLocation.index
    }

    return orderedQuestions.filter((question) => activeQuestionIds.has(question.id))
  }

  const handleSubmit = async () => {
    if (isUploading) {
      toast.error(uiText('Aguarde o upload do áudio.', 'Wait for the audio upload.', 'Espere la carga del audio.'))
      return
    }
    if (!audioDecisionMade) {
      toast.error(uiText(
        'Escolha no início se esta resposta será gravada ou não.',
        'Choose at the beginning whether this response will be recorded or not.',
        'Elija al inicio si esta respuesta sera grabada o no.'
      ))
      return
    }

    const currentQuestionError = currentQuestion && !terminalPage
      ? getQuestionValidationError(currentQuestion, answers[currentQuestion.id])
      : null
    if (currentQuestionError) {
      if (currentQuestion) setValidationAlertQuestionId(currentQuestion.id)
      toast.error(currentQuestionError, { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      return
    }

    if (currentQuestion?.required && !terminalPage && !isQuestionAnswerComplete(currentQuestion, answers[currentQuestion.id], orderedQuestions, answers)) {
      setValidationAlertQuestionId(currentQuestion.id)
      toast.error(uiText('Esta pergunta é obrigatória.', 'This question is required.', 'Esta pregunta es obligatoria.'), { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      return
    }

    const activePathQuestions = terminalPage ? [] : getActivePathQuestions()
    const unanswered = terminalPage ? [] : getUnansweredRequiredQuestions(activePathQuestions, answers)
    if (unanswered.length > 0) {
      setValidationAlertQuestionId(unanswered[0].id)
      toast.error(uiText(
        `Responda todas as perguntas obrigatórias (${unanswered.length} pendente${unanswered.length > 1 ? 's' : ''}).`,
        `Answer all required questions (${unanswered.length} pending).`,
        `Responda todas las preguntas obligatorias (${unanswered.length} pendiente${unanswered.length > 1 ? 's' : ''}).`
      ), { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      const firstIndex = indexMap.get(unanswered[0].id)
      if (firstIndex !== undefined) {
        setTerminalPage(null)
        setCurIdx(firstIndex)
      }
      return
    }

    const questionWithValidationError = activePathQuestions.find((question) => (
      evaluateDisplayConditions(question, orderedQuestions, answers) &&
      !!getQuestionValidationError(question, answers[question.id])
    ))

    if (questionWithValidationError) {
      setValidationAlertQuestionId(questionWithValidationError.id)
      toast.error(getQuestionValidationError(questionWithValidationError, answers[questionWithValidationError.id]) || uiText(
        'Revise a resposta desta pergunta.',
        'Review the answer for this question.',
        'Revise la respuesta de esta pregunta.'
      ), { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      const targetIndex = indexMap.get(questionWithValidationError.id)
      if (targetIndex !== undefined) {
        setTerminalPage(null)
        setCurIdx(targetIndex)
      }
      return
    }

    const questionWithSelectionError = activePathQuestions.find((question) => (
      evaluateDisplayConditions(question, orderedQuestions, answers) &&
      !!getSelectionValidationError(question, answers[question.id])
    ))

    if (questionWithSelectionError) {
      setValidationAlertQuestionId(questionWithSelectionError.id)
      toast.error(getSelectionValidationError(questionWithSelectionError, answers[questionWithSelectionError.id]) || uiText(
        'Revise a seleção desta pergunta.',
        'Review the selection for this question.',
        'Revise la seleccion de esta pregunta.'
      ), { id: SURVEY_VALIDATION_TOAST_ID, duration: 3000 })
      const targetIndex = indexMap.get(questionWithSelectionError.id)
      if (targetIndex !== undefined) {
        setTerminalPage(null)
        setCurIdx(targetIndex)
      }
      return
    }

    setValidationAlertQuestionId(null)
    toast.dismiss(SURVEY_VALIDATION_TOAST_ID)

    setSubmitting(true)
    const submittedAt = new Date().toISOString()
    // Cancelar por filtro tambem quando o desvio dispara na ultima pergunta
    // respondida (nesse caso o botao ja mostra Enviar e a tela terminal nao
    // chega a ser exibida).
    const cancelledByFilter = terminalPage?.terminalOutcome === 'cancel_filter' ||
      (nextLocation?.kind === 'terminal' && nextLocation.page.terminalOutcome === 'cancel_filter')

    if (testMode) {
      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      toast.success(uiText(
        'Teste concluído. Nenhuma resposta foi gravada.',
        'Test completed. No response was saved.',
        'Prueba concluida. No se guardo ninguna respuesta.'
      ))
      onSubmitted()
      setSubmitting(false)
      return
    }

    // Se estiver offline previamente, enfileira diretamente sem esperar timeout de rede
    if (!navigator.onLine) {
      let audioDataUrl: string | null = null
      let audioFileName: string | null = null
      let audioDurationSec: number | null = null

      if (audioConsent && recorderRef.current && recorderRef.current.state !== 'inactive') {
        try {
          const recorder = recorderRef.current
          const mimeType = recorder.mimeType || getSupportedMime() || 'audio/webm'
          const durationSec = recordingSecondsRef.current
          const blob = await new Promise<Blob>((resolve, reject) => {
            recorder.onstop = () => {
              try {
                resolve(new Blob(chunksRef.current, { type: mimeType }))
              } catch (err) {
                reject(err)
              }
            }
            recorder.onerror = () => reject(new Error('Erro ao finalizar gravação'))
            try { recorder.stop() } catch (err) { reject(err) }
          })
          const mp3Blob = await convertRecordedAudioToMp3(blob)
          const fileName = buildAudioFileName(survey._id, `off_${Date.now()}`, 'audio/mpeg')
          audioDataUrl = await blobToBase64(mp3Blob)
          audioFileName = fileName
          audioDurationSec = durationSec
        } catch (audioErr) {
          console.warn('Falha ao processar áudio offline:', audioErr)
        } finally {
          clearRecordingInterval()
          stopStream()
          chunksRef.current = []
          recorderRef.current = null
          recordingSecondsRef.current = 0
          setRecordingSeconds(0)
          setIsRecording(false)
          setIsUploading(false)
        }
      }

      const offlineId = `off_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      const offlineItem: OfflineSubmission = {
        id: offlineId,
        surveyId: survey._id,
        surveyTitle: survey.title,
        researcherId: researcherId || 'public_link',
        researcherName: researcherName || '',
        language,
        answers: buildResponseAnswers(),
        audioConsent,
        audioConsentAt: audioConsentAt || null,
        audioDataUrl,
        audioFileName,
        audioDurationSec,
        completedAt: submittedAt,
        startedAt: submittedAt,
        savedAt: new Date().toISOString(),
        isCancelled: cancelledByFilter,
        cancellationReason: cancelledByFilter ? 'filter' : undefined,
      }

      addOfflineSubmission(offlineItem)
      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      updateOfflineCount()
      setSubmitting(false)

      toast.success(uiText(
        'Sem conexão. Sua resposta foi salva na fila deste aparelho e será enviada quando reconectar.',
        'No connection. Your response was saved to this device queue and will be sent when you reconnect.',
        'Sin conexion. Su respuesta fue guardada en la cola de este dispositivo y se enviara cuando se reconecte.'
      ), { duration: 6000 })

      if (isPublicLink) {
        onSubmitted()
      } else {
        const offlineFile = audioDataUrl
          ? base64ToFile(audioDataUrl, audioFileName || `audio_${offlineId}.mp3`)
          : null

        if (offlineFile) {
          saveAudioToIndexedDB(offlineId, offlineFile, audioFileName || `audio_${offlineId}.mp3`, offlineId)
        }

        setSubmittedResult({
          responseId: offlineId,
          audioSaved: !audioConsent || !!audioDataUrl,
          audio: offlineFile ? {
            responseId: offlineId,
            audioUrl: audioDataUrl,
            remoteAudioUrl: '',
            audioName: audioFileName || `audio_${offlineId}.mp3`,
            durationSec: audioDurationSec || 0,
            submittedAt,
            blob: offlineFile,
            file: offlineFile,
          } : null,
        })
      }
      return
    }

    try {
      const responseData: Record<string, any> = {
        surveyId: survey._id,
        surveyTitle: survey.title,
        researcherId,
        researcherName: researcherName || '',
        language,
        status: cancelledByFilter ? 'cancelled' : 'completed',
        cancellationReason: cancelledByFilter ? 'filter' : undefined,
        submittedAt,
        completedAt: cancelledByFilter ? undefined : submittedAt,
        cancelledAt: cancelledByFilter ? submittedAt : undefined,
        createdAt: submittedAt,
        updatedAt: submittedAt,
        audio_consent: audioConsent,
        audio_consentAt: audioConsentAt || undefined,
        answers: buildResponseAnswers(),
      }

      const createdResponse = await lumi.entities.responses.create(responseData) as { _id: string }
      let audioSaved = !audioConsent
      let completedAudio: SubmittedAudio | null = null

      if (audioConsent) {
        try {
          const audioData = await finishRecordingForResponse(createdResponse._id)
          if (audioData) {
            const {
              audioBlob,
              audioFile,
              audioPreviewUrl,
              ...responseAudioData
            } = audioData

            let base64Audio: string | undefined = undefined
            try {
              base64Audio = await blobToBase64(audioBlob)
            } catch {
              // ignore
            }

            await saveAudioToIndexedDB(
              createdResponse._id,
              audioBlob,
              responseAudioData.audio_nome,
              createdResponse._id,
              responseAudioData.audio_url
            )

            await lumi.entities.responses.update(createdResponse._id, {
              ...responseAudioData,
              audio_data: base64Audio,
            })
            audioSaved = true
            completedAudio = {
              responseId: createdResponse._id,
              audioUrl: audioPreviewUrl,
              remoteAudioUrl: responseAudioData.audio_url,
              audioName: responseAudioData.audio_nome,
              durationSec: responseAudioData.duração_audio,
              submittedAt,
              blob: audioBlob,
              file: audioFile,
            }
          }
        } catch (audioError) {
          console.error('Erro ao salvar áudio da resposta:', audioError)
          audioSaved = false
        }
      }

      if (!isPublicLink && researcherId !== 'public' && researcherId !== 'public_link') {
        try {
          const { list: assignments } = await lumi.entities.survey_assignments.list({
            filter: { surveyId: survey._id, researcherId },
          }) as { list: any[] }

          const assignment = (assignments || []).find((item: any) => item.status !== 'completed' && item.status !== 'removed')
          if (assignment) {
            await lumi.entities.survey_assignments.update(assignment._id, {
              // Entrevista cancelada por filtro NAO conclui a atribuicao:
              // ela permanece em andamento para uma nova tentativa.
              status: cancelledByFilter ? 'in_progress' : 'completed',
              completedAt: cancelledByFilter ? undefined : submittedAt,
              startedAt: assignment.startedAt || submittedAt,
              updatedAt: submittedAt,
            })
          }
        } catch {
          // não bloqueia
        }

        try {
          const { list: allAssignments } = await lumi.entities.survey_assignments.list({
            filter: { researcherId },
          }) as { list: any[] }

          const completedCount = (allAssignments || []).filter((item: any) => item.status === 'completed').length
          const assignedCount = (allAssignments || []).filter((item: any) => item.status !== 'removed').length
          await lumi.entities.researchers.update(researcherId, {
            assignedSurveys: assignedCount,
            completedSurveys: completedCount,
            updatedAt: submittedAt,
          })
        } catch {
          // não bloqueia
        }
      }

      clearDraft()
      resetSurveyProgress()
      resetAudioChoice()
      setSubmittedResult({
        responseId: createdResponse._id,
        audioSaved,
        audio: completedAudio,
      })
      if (audioConsent && !audioSaved) {
        toast.success(uiText('Resposta enviada. O áudio não foi salvo.', 'Response submitted. The audio was not saved.', 'Respuesta enviada. El audio no fue guardado.'))
      } else {
        toast.success(uiText('Resposta enviada com sucesso!', 'Response submitted successfully!', 'Respuesta enviada con exito!'))
      }
    } catch (error) {
      console.error('Erro ao enviar resposta:', error)
      if (isNetworkError(error)) {
        let audioDataUrl: string | null = null
        let audioFileName: string | null = null
        let audioDurationSec: number | null = null

        if (audioConsent && recorderRef.current && recorderRef.current.state !== 'inactive') {
          try {
            const recorder = recorderRef.current
            const mimeType = recorder.mimeType || getSupportedMime() || 'audio/webm'
            const durationSec = recordingSecondsRef.current
            const blob = await new Promise<Blob>((resolve, reject) => {
              recorder.onstop = () => {
                try {
                  resolve(new Blob(chunksRef.current, { type: mimeType }))
                } catch (err) {
                  reject(err)
                }
              }
              recorder.onerror = () => reject(new Error('Erro ao finalizar gravação'))
              try { recorder.stop() } catch (err) { reject(err) }
            })
            const mp3Blob = await convertRecordedAudioToMp3(blob)
            const fileName = buildAudioFileName(survey._id, `off_${Date.now()}`, 'audio/mpeg')
            audioDataUrl = await blobToBase64(mp3Blob)
            audioFileName = fileName
            audioDurationSec = durationSec
          } catch (audioErr) {
            console.warn('Falha ao converter áudio offline após erro de rede:', audioErr)
          } finally {
            clearRecordingInterval()
            stopStream()
            chunksRef.current = []
            recorderRef.current = null
            recordingSecondsRef.current = 0
            setRecordingSeconds(0)
            setIsRecording(false)
            setIsUploading(false)
          }
        }

        const offlineId = `off_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        const offlineItem: OfflineSubmission = {
          id: offlineId,
          surveyId: survey._id,
          surveyTitle: survey.title,
          researcherId: researcherId || 'public_link',
          researcherName: researcherName || '',
          language,
          answers: buildResponseAnswers(),
          audioConsent,
          audioConsentAt: audioConsentAt || null,
          audioDataUrl,
          audioFileName,
          audioDurationSec,
          completedAt: submittedAt,
          startedAt: submittedAt,
          savedAt: new Date().toISOString(),
          isCancelled: cancelledByFilter,
          cancellationReason: cancelledByFilter ? 'filter' : undefined,
        }

        addOfflineSubmission(offlineItem)
        clearDraft()
        resetSurveyProgress()
        resetAudioChoice()
        updateOfflineCount()

        toast.success(uiText(
          'Sem conexão. Sua resposta foi salva na fila deste aparelho e será enviada quando reconectar.',
          'No connection. Your response was saved to this device queue and will be sent when you reconnect.',
          'Sin conexion. Su respuesta fue guardada en la cola de este dispositivo y se enviara cuando se reconecte.'
        ), { duration: 6000 })

        let offlineFile: File | null = null
        if (audioDataUrl) {
          try {
            offlineFile = base64ToFile(audioDataUrl, audioFileName || `audio_${offlineId}.mp3`)
            saveAudioToIndexedDB(offlineId, offlineFile, audioFileName || `audio_${offlineId}.mp3`, offlineId)
          } catch (e) {
            console.warn('Erro ao criar arquivo de áudio offline:', e)
          }
        }

        if (isPublicLink) {
          onSubmitted()
        } else {
          setSubmittedResult({
            responseId: offlineId,
            audioSaved: !audioConsent || !!audioDataUrl,
            audio: offlineFile ? {
              responseId: offlineId,
              audioUrl: audioDataUrl || '',
              remoteAudioUrl: '',
              audioName: audioFileName || `audio_${offlineId}.mp3`,
              durationSec: audioDurationSec || 0,
              submittedAt,
              blob: offlineFile,
              file: offlineFile,
            } : null,
          })
        }
      } else {
        toast.error(uiText('Erro ao enviar resposta. Tente novamente.', 'Error submitting response. Try again.', 'Error al enviar la respuesta. Intente nuevamente.'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (orderedQuestions.length === 0) {
    return (
      <div className={isPublicLink ? "min-h-screen w-full bg-slate-50 flex items-center justify-center p-4" : "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"}>
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl border border-gray-100">
          <p className="mb-4 text-gray-500">
            {uiText('Esta pesquisa não possui perguntas.', 'This survey has no questions.', 'Esta encuesta no tiene preguntas.')}
          </p>
          {!isPublicLink && onClose && (
            <button onClick={handleClose} className="rounded-lg bg-gray-200 px-4 py-2 text-sm">
              {uiText('Fechar', 'Close', 'Cerrar')}
            </button>
          )}
        </div>
      </div>
    )
  }

  if (submittedResult) {
    const submitContainerClasses = isPublicLink
      ? "min-h-screen w-full bg-slate-50 flex items-stretch justify-center p-0 sm:p-4"
      : "fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 p-0 sm:p-3 lg:p-4"

    const submitInnerClasses = isPublicLink
      ? "flex w-full max-w-2xl flex-col bg-white shadow-xl min-h-[calc(100vh-2rem)] sm:rounded-2xl border border-gray-100"
      : "flex h-full w-full flex-col bg-white shadow-2xl sm:h-[calc(100vh-1.5rem)] sm:w-full sm:max-w-2xl sm:rounded-2xl"

    return (
      <div className={submitContainerClasses}>
        <div className={submitInnerClasses}>
          <div className="flex items-start justify-between border-b border-gray-100 px-4 py-4 sm:px-6">
            <div className="min-w-0 flex-1">
              <h2 className="break-normal text-base font-semibold text-gray-900 [overflow-wrap:normal]">
                {uiText('Resposta enviada', 'Response submitted', 'Respuesta enviada')}
              </h2>
              <p className="mt-0.5 break-normal text-xs text-gray-400 [overflow-wrap:normal]">
                {uiText('A pesquisa foi finalizada com sucesso.', 'The survey was completed successfully.', 'La encuesta foi finalizada con exito.')}
              </p>
            </div>
            {!isPublicLink && onClose && (
              <button
                onClick={closeSubmittedResult}
                className="ml-4 shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
              <div className="mb-3 inline-flex rounded-full bg-emerald-100 p-3 text-emerald-600">
                <CheckCircle size={26} />
              </div>
              <p className="text-base font-semibold text-emerald-900">
                {uiText('Coleta concluída', 'Collection completed', 'Recoleccion concluida')}
              </p>
              <p className="mt-1 break-normal text-sm text-emerald-800 [overflow-wrap:normal]">
                {submittedResult.audio
                  ? uiText('O áudio ficou disponível abaixo para ouvir, baixar ou compartilhar.', 'The audio is available below to listen, download, or share.', 'El audio esta disponible abajo para escuchar, descargar o compartir.')
                  : submittedResult.audioSaved
                    ? uiText('A resposta foi registrada sem gravação de áudio.', 'The response was saved without audio recording.', 'La respuesta fue guardada sin grabacion de áudio.')
                    : uiText('A resposta foi registrada, mas o áudio não foi salvo.', 'The response was saved, but the audio was not saved.', 'La respuesta fue guardada, pero el audio no fue guardado.')}
              </p>
            </div>

            {submittedResult.audio && (
              <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                <div className="space-y-1 text-sm text-blue-900">
                  <p className="break-normal font-semibold [overflow-wrap:normal]">{submittedResult.audio.audioName}</p>
                  <p className="text-xs text-blue-700">
                    {uiText('Duração', 'Duration', 'Duracion')}: {formatDuration(submittedResult.audio.durationSec)}
                  </p>
                </div>

                <audio controls preload="metadata" src={submittedResult.audio.audioUrl} className="mt-4 w-full" />

                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={downloadSubmittedAudio}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <Download size={15} />
                    {uiText('Baixar áudio', 'Download audio', 'Descargar audio')}
                  </button>
                  <button
                    type="button"
                    onClick={shareSubmittedAudio}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                  >
                    <Share2 size={15} />
                    {uiText('Compartilhar áudio', 'Share audio', 'Compartir audio')}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 px-4 py-4 sm:px-6 flex flex-col sm:flex-row items-center justify-end gap-3">
            <button
              type="button"
              onClick={restartNewInterview}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-5 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100"
            >
              <RefreshCw size={15} />
              {uiText('Iniciar Nova Entrevista', 'Start New Interview', 'Iniciar Nueva Entrevista')}
            </button>
            <button
              type="button"
              onClick={closeSubmittedResult}
              className="w-full sm:w-auto flex items-center justify-center rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700"
            >
              {uiText('Concluir', 'Finish', 'Concluir')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const containerClasses = isPublicLink
    ? "min-h-screen w-full bg-slate-50 flex items-stretch justify-center p-0 sm:p-4"
    : "fixed inset-0 z-50 flex items-stretch justify-center overflow-x-hidden bg-black/50 p-0 sm:p-3 lg:p-4"

  const innerClasses = isPublicLink
    ? "flex w-full max-w-4xl flex-col bg-white shadow-xl min-h-[calc(100vh-2rem)] sm:rounded-2xl border border-gray-100"
    : "flex h-full w-full max-w-full flex-col bg-white shadow-2xl sm:h-[calc(100vh-1.5rem)] sm:w-[calc(100vw-1.5rem)] sm:rounded-2xl lg:h-[calc(100vh-2rem)] lg:w-[calc(100vw-2rem)]"

  return (
    <div className={containerClasses}>
      <div className={innerClasses}>
        {(isOffline || offlineCount > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 sm:px-6">
            <div className="flex items-center gap-2 text-xs font-medium text-amber-900">
              <WifiOff size={15} className="shrink-0 text-amber-600" />
              <span>
                {isOffline
                  ? uiText(
                      'Modo Offline ativo: suas respostas serão guardadas neste aparelho e enviadas quando reconectar.',
                      'Offline Mode active: your answers will be saved on this device and sent when reconnected.',
                      'Modo Offline activo: sus respuestas se guardaran en este dispositivo y se enviaran cuando se reconecte.'
                    )
                  : uiText(
                      `Conexão restaurada. Existem ${offlineCount} resposta(s) na fila prontas para envio.`,
                      `Connection restored. There are ${offlineCount} response(s) in queue ready to sync.`,
                      `Conexion restaurada. Existen ${offlineCount} respuesta(s) en la cola listas para enviar.`
                    )}
              </span>
            </div>
            {!isOffline && offlineCount > 0 && (
              <button
                type="button"
                disabled={isSyncingOffline}
                onClick={async () => {
                  setIsSyncingOffline(true)
                  try {
                    const res = await syncOfflineSubmissions({ surveyId: survey._id })
                    if (res.sent > 0) {
                      toast.success(uiText(
                        `${res.sent} resposta(s) enviada(s) com sucesso!`,
                        `${res.sent} response(s) sent successfully!`,
                        `¡${res.sent} respuesta(s) enviada(s) con exito!`
                      ))
                    }
                  } catch {
                    toast.error(uiText('Falha ao sincronizar. Tente novamente.', 'Sync failed. Try again.', 'Fallo al sincronizar. Intente nuevamente.'))
                  } finally {
                    setIsSyncingOffline(false)
                    updateOfflineCount()
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 px-3 py-1 text-xs font-bold text-white transition-colors disabled:opacity-50"
              >
                {isSyncingOffline ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                <span>{uiText('Sincronizar fila agora', 'Sync queue now', 'Sincronizar cola ahora')}</span>
              </button>
            )}
          </div>
        )}
        <div className="flex items-start justify-between border-b border-gray-100 px-4 py-4 sm:px-6">
          <div className="min-w-0 flex-1">
            {testMode && (
              <span className="mb-2 inline-flex rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-800">
                Modo teste
              </span>
            )}
            {survey.showSurveyTitle !== false && (
              <RichTextBlock
                as="h2"
                html={surveyTitleHtml}
                fallbackAlign="left"
                className="break-normal text-base font-semibold text-gray-900 [overflow-wrap:normal] [word-break:normal] [&_*]:max-w-full"
              />
            )}
            <p className="mt-0.5 text-xs text-gray-400">
              {terminalPage
                ? uiText('Tela final da pesquisa', 'Final survey screen', 'Pantalla final de la encuesta')
                : showIntroduction
                  ? uiText('Introdução da pesquisa', 'Survey introduction', 'Introduccion de la encuesta')
                  : uiText(
                    `Pergunta ${curIdx + 1} de ${orderedQuestions.length}`,
                    `Question ${curIdx + 1} of ${orderedQuestions.length}`,
                    `Pregunta ${curIdx + 1} de ${orderedQuestions.length}`
                  )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancelSurvey}
            disabled={isUploading || submitting}
            className="ml-4 inline-flex shrink-0 items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 shadow-sm transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={16} />
            {uiText('Cancelar', 'Cancel', 'Cancelar')}
          </button>
        </div>

        {availableLanguages.length > 1 && (
          <div className="border-b border-gray-100 px-4 py-3 sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                {uiText('Idioma da resposta', 'Response language', 'Idioma de la respuesta')}
              </span>
              {availableLanguages.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLanguage(item)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    language === item
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {SURVEY_LANGUAGE_LABELS[item]}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="h-1 w-full bg-gray-100">
          <div className="h-1 bg-blue-500 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6">
          {!audioDecisionMade ? (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
              <p className="text-sm font-semibold text-blue-900">
                {uiText('Deseja gravar esta resposta inteira?', 'Do you want to record this entire response?', 'Desea grabar esta respuesta completa?')}
              </p>
              <p className="mt-1 text-sm text-blue-800">
                {uiText(
                  'Se escolher gravar, o áudio começa agora e continua até o encerramento da pesquisa.',
                  'If you choose to record, the audio starts now and continues until the survey ends.',
                  'Si elige grabar, el audio comienza ahora y continua hasta el cierre de la encuesta.'
                )}
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={startRecording}
                  disabled={isUploading || submitting}
                  className="flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Mic size={15} />
                  {uiText('Iniciar com gravação', 'Start with recording', 'Iniciar con grabacion')}
                </button>
                <button
                  onClick={chooseWithoutAudio}
                  disabled={isUploading || submitting}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {uiText('Responder sem gravação', 'Answer without recording', 'Responder sin grabacion')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {translationLoading && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-800">
                  {uiText('Traduzindo conteúdo da pesquisa...', 'Translating survey content...', 'Traduciendo contenido de la encuesta...')}
                </div>
              )}

              {audioConsent && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="flex items-center gap-2 text-sm font-semibold text-red-800">
                        <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" />
                        {uiText('Gravação em andamento', 'Recording in progress', 'Grabacion en curso')}
                      </p>
                      <p className="mt-1 text-xs text-red-700">
                        {uiText(
                          `Esta resposta está sendo gravada por completo. Tempo atual: ${formatDuration(recordingSeconds)}`,
                          `This response is being fully recorded. Current time: ${formatDuration(recordingSeconds)}`,
                          `Esta respuesta se esta grabando por completo. Tiempo actual: ${formatDuration(recordingSeconds)}`
                        )}
                      </p>
                    </div>
                    <button
                      onClick={chooseWithoutAudio}
                      disabled={isUploading || submitting}
                      className="rounded-lg border border-red-300 bg-white px-3 py-2 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {uiText('Cancelar gravação desta resposta', 'Cancel recording for this response', 'Cancelar grabacion de esta respuesta')}
                    </button>
                  </div>
                </div>
              )}

              {shouldShowLogoInCurrentScreen && (
                <div className="flex justify-center">
                  <div className={`overflow-hidden rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${getLogoDisplayWidthClass(survey.logoDisplaySize || 'medium')}`}>
                    <img
                      src={surveyLogoUrl}
                      alt={stripHtml(surveyTitleHtml) || 'Logo da pesquisa'}
                      className={`mx-auto h-auto w-full object-contain ${getLogoDisplayImageClass(survey.logoDisplaySize || 'medium')}`}
                    />
                  </div>
                </div>
              )}

              {showIntroduction ? (
                <div className="min-w-0 max-w-full space-y-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6">
                  {surveyLogoUrl ? (
                    <div className="flex justify-center">
                      <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${getLogoDisplayWidthClass(survey.logoDisplaySize || 'medium')}`}>
                        <img
                          src={surveyLogoUrl}
                          alt={stripHtml(surveyTitleHtml) || 'Logo da pesquisa'}
                          className={`mx-auto h-auto w-full object-contain ${getLogoDisplayImageClass(survey.logoDisplaySize || 'medium')}`}
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="min-w-0 max-w-full space-y-3">
                    <p className="text-center text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                      {uiText('Introdução', 'Introduction', 'Introduccion')}
                    </p>
                    {survey.showSurveyTitle !== false && (
                      <RichTextBlock
                        as="h3"
                        html={surveyTitleHtml}
                        fallbackAlign="center"
                        className="break-normal text-xl font-semibold leading-tight text-slate-900 [overflow-wrap:normal] [word-break:normal] [&_*]:max-w-full [&_.ql-align-center]:text-center [&_.ql-align-justify]:text-justify [&_.ql-align-left]:text-left [&_.ql-align-right]:text-right"
                      />
                    )}
                    {surveyDescriptionHtml ? (
                      <RichTextBlock
                        html={surveyDescriptionHtml}
                        fallbackAlign="left"
                        className="w-full break-normal text-sm leading-6 text-slate-600 [overflow-wrap:normal] [word-break:normal] [&_*]:max-w-full [&_.ql-align-center]:text-center [&_.ql-align-justify]:text-justify [&_.ql-align-left]:text-left [&_.ql-align-right]:text-right"
                      />
                    ) : (
                      <p className="mx-auto max-w-xl text-sm leading-6 text-slate-500">
                        {uiText(
                          'Leia a apresentação e avance para iniciar as perguntas da pesquisa.',
                          'Read the presentation and continue to start the survey questions.',
                          'Lea la presentacion y avance para iniciar las preguntas de la encuesta.'
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ) : terminalPage ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                  <div className="mb-3 inline-flex rounded-full bg-emerald-100 p-3 text-emerald-600">
                    <CheckCircle size={24} />
                  </div>
                  <RichTextBlock
                    as="h3"
                    html={terminalPageTitleHtml}
                    fallbackAlign="center"
                    className="text-lg font-semibold text-emerald-900"
                  />
                  {terminalPageDescriptionHtml ? (
                    <RichTextBlock
                      html={terminalPageDescriptionHtml}
                      fallbackAlign="center"
                      className="mt-2 text-sm text-emerald-800"
                    />
                  ) : (
                    <p className="mt-2 text-sm text-emerald-800">
                      {uiText(
                        'Você chegou ao encerramento desta pesquisa.',
                        'You have reached the end of this survey.',
                        'Ha llegado al final de esta encuesta.'
                      )}
                    </p>
                  )}
                </div>
              ) : currentQuestion ? (
                <div className="space-y-4">
                  {shouldShowCurrentPageTitle && (
                    <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                      <RichTextBlock
                        as="h3"
                        html={currentPageTitleHtml}
                        fallbackAlign="left"
                        className="break-normal text-sm font-semibold text-blue-900 [overflow-wrap:normal] [&_*]:max-w-full"
                      />
                      {currentPageDescriptionHtml && (
                        <RichTextBlock
                          html={currentPageDescriptionHtml}
                          fallbackAlign="left"
                          className="mt-1 break-normal text-xs leading-5 text-blue-800 [overflow-wrap:normal] [&_*]:max-w-full"
                        />
                      )}
                    </div>
                  )}
                  {currentQuestionImageUrl && (
                    <div className={`overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-3 ${getImageDisplayWidthClass(currentQuestion.imageDisplaySize)}`}>
                      <img
                        src={currentQuestionImageUrl}
                        alt={stripHtml(translatedCurrentQuestionTitleHtml)}
                        className="h-auto w-full rounded-xl object-contain"
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        {uiText(
                          `Tamanho configurado: ${getImageDisplayHint(currentQuestion.imageDisplaySize)}`,
                          `Configured size: ${autoTranslateText(getImageDisplayHint(currentQuestion.imageDisplaySize), 'en')}`,
                          `Tamano configurado: ${autoTranslateText(getImageDisplayHint(currentQuestion.imageDisplaySize), 'es')}`
                        )}
                      </p>
                    </div>
                  )}

                  <div>
                    <RichTextBlock
                      as="h3"
                      html={translatedCurrentQuestionTitleHtml}
                      fallbackAlign="left"
                      className="text-base font-semibold leading-snug text-gray-800"
                    />
                    {currentQuestion.required && (
                      <span className="ml-1 text-xs text-red-500">
                        {uiText('*obrigatória', '*required', '*obligatoria')}
                      </span>
                    )}
                    {validationAlertQuestionId === currentQuestion.id && currentQuestion.required && !isQuestionAnswerComplete(currentQuestion, answers[currentQuestion.id], orderedQuestions, answers) && (
                      <div className="mt-2.5 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs font-semibold text-red-700 shadow-sm animate-in fade-in">
                        <div className="flex items-center gap-2">
                          <AlertCircle size={15} className="shrink-0 text-red-600" />
                          <span>{uiText('Esta pergunta é obrigatória.', 'This question is required.', 'Esta pregunta es obligatoria.')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setValidationAlertQuestionId(null)
                            toast.dismiss(SURVEY_VALIDATION_TOAST_ID)
                          }}
                          className="ml-2 rounded p-0.5 text-red-400 hover:bg-red-100 hover:text-red-700"
                          title={uiText('Fechar aviso', 'Close warning', 'Cerrar aviso')}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    {translatedCurrentQuestionDescriptionHtml && (
                      <RichTextBlock
                        html={translatedCurrentQuestionDescriptionHtml}
                        fallbackAlign="left"
                        className="mt-1 break-normal text-sm text-gray-500 [overflow-wrap:normal] [&_*]:max-w-full [&_.ql-align-center]:text-center [&_.ql-align-justify]:text-justify [&_.ql-align-left]:text-left [&_.ql-align-right]:text-right"
                      />
                    )}
                    {getSelectionValidationError(currentQuestion, answers[currentQuestion.id]) && (
                      <p className="mt-2 text-xs font-medium text-amber-600">
                        {getSelectionValidationError(currentQuestion, answers[currentQuestion.id])}
                      </p>
                    )}
                  </div>

                  <AnswerInput
                    question={currentQuestion}
                    questions={orderedQuestions}
                    answers={answers}
                    answer={answers[currentQuestion.id]}
                    language={language}
                    translatedTexts={translatedTexts}
                    onChange={(value) => handleAnswer(currentQuestion.id, value)}
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  {uiText('Nenhuma pergunta disponível.', 'No question available.', 'No hay preguntas disponibles.')}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-1.5 py-2">
          {[showIntroduction ? 'intro' : null, ...orderedQuestions.map((_, index) => index)]
            .filter((item) => item !== null)
            .map((item, index) => (
            <span
              key={String(item)}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                showIntroduction
                  ? index === 0
                    ? 'bg-blue-600'
                    : 'bg-gray-300'
                  : index === curIdx + 1
                    ? 'bg-blue-600'
                    : index < curIdx + 1
                      ? 'bg-green-500'
                      : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <button
            onClick={handlePrev}
            disabled={!audioDecisionMade || isFirstStep || isUploading || submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
          >
            <ChevronLeft size={16} />
            {uiText('Anterior', 'Previous', 'Anterior')}
          </button>

          {!isFinalStep ? (
            <button
              onClick={handleNext}
              disabled={!audioDecisionMade || isUploading || submitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-40 sm:w-auto"
            >
              {showIntroduction
                ? uiText('Começar pesquisa', 'Start survey', 'Comenzar encuesta')
                : uiText('Próxima', 'Next', 'Siguiente')}
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!audioDecisionMade || submitting || isUploading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-40 sm:w-auto"
            >
              {submitting ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {uiText('Enviando...', 'Submitting...', 'Enviando...')}
                </>
              ) : (
                <>
                  <Send size={15} />
                  {testMode
                    ? uiText('Concluir Teste', 'Finish Test', 'Concluir prueba')
                    : uiText('Enviar Resposta', 'Submit Response', 'Enviar respuesta')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResearcherSurveyModal